/**
 * Platform admin API client.
 * All calls use credentials:'include' (the HTTP-only platform cookie:
 * ``bp_platform`` in prod regions, ``bp_platform_<region>`` in non-prod
 * envs like dev — the browser sends it by name automatically).
 *
 * Region routing: in multi-region mode, the RegionProvider sets a base URL
 * on regionStore (e.g. ``https://admin.us.bolloprint.com``) so calls
 * targeting another region work transparently. Single-region mode leaves
 * the base URL empty and behaviour is identical to before.
 */
import { withApiBaseUrl } from './regionStore';
import { safeUploadFetch, ensureFileReadable } from '@shared/lib/fileUpload';
import { browserRetryEnv, isNetworkUploadError, uploadMultipartWithRetry, UploadRefusedError, type UploadState } from '@shared/lib/upload/retry';
import { UPLOAD_NETWORK_ERROR_ES } from '@shared/lib/upload/errorText';
import { browserPostForm } from '@shared/lib/upload/xhr';
import { MAX_SIZE_DEV_ATTACHMENT, MAX_SIZE_IMAGE } from '@shared/lib/fileValidation';

/** Caps of the admin's upload routes that are not in `fileValidation`, the
 *  same numbers the backend checks (`core/body_limits.py` and each route). */
const MB = 1024 * 1024;
const MAX_SUPPORT_ATTACHMENT = 8 * MB;
const MAX_PARTNER_PDF = 15 * MB;
const MAX_EMAIL_IMAGE = 5 * MB;
const tooLargeMessage = (maxBytes: number) =>
  `El archivo pesa más de ${Math.floor(maxBytes / MB)} MB, el máximo aquí. Redúcelo o divídelo en partes.`;

/** Progress + cancel for an upload (the ring on the file, `useUploads`). */
export type UploadOptions = { onState?: (state: UploadState) => void; signal?: AbortSignal };

/** A multipart upload to the admin with progress and retries (shared engine);
 *  a refusal becomes an Error with the backend's readable message. */
async function uploadToAdmin<T>(
  path: string,
  file: File,
  /** The route's cap (the server's, `core/body_limits.py`): a bigger file is
   *  refused before a byte is sent. Required, so no upload forgets it. */
  maxBytes: number,
  options: UploadOptions & { noRetry?: boolean; fields?: Record<string, string> },
  extraHeaders: Record<string, string> = {},
): Promise<T> {
  try {
    return await uploadMultipartWithRetry(
      file,
      { url: withApiBaseUrl(path), withCredentials: true, headers: extraHeaders, fields: options.fields },
      { readable: ensureFileReadable, post: browserPostForm() },
      browserRetryEnv(),
      { ...options, maxBytes },
    ) as T;
  } catch (err) {
    if (err instanceof UploadRefusedError) throw new Error(extractApiError({ detail: err.detail }, err.status), { cause: err });
    // Every retry ran out: say it in Spanish, not the engine's «post status 0».
    if (isNetworkUploadError(err)) throw new Error(UPLOAD_NETWORK_ERROR_ES, { cause: err });
    throw err;
  }
}
import type { CountryEntry, CountryRegion } from '@shared/lib/countries';

export type { CountryEntry, CountryRegion, CountryTax } from '@shared/lib/countries';

/**
 * Normalize a JSON error body into a human-readable string.
 *
 * FastAPI returns explicit errors as ``{detail: "..."}`` (a string), but
 * 422 validation errors as ``{detail: [{loc, msg, type}, ...]}`` — an array
 * of objects. Passing that array straight into ``new Error(...)`` renders as
 * ``[object Object]`` in the UI (the bug this fixes). This flattens every
 * shape — ``error`` string, ``detail`` string, 422 array, or a nested object
 * — into a clean message like ``"brand_specific_package_id: String should
 * have at most 16 characters"``.
 */
export function extractApiError(body: unknown, status: number): string {
  if (!body || typeof body !== 'object') return `Error ${status}`;
  const b = body as { error?: unknown; detail?: unknown };
  if (typeof b.error === 'string' && b.error) return b.error;
  const detail = b.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (!d || typeof d !== 'object') return String(d ?? '');
        const loc = Array.isArray(d.loc)
          ? d.loc.filter((p: unknown) => p !== 'body').join('.')
          : '';
        const msg = d.msg ?? d.message ?? '';
        return loc ? `${loc}: ${msg}` : String(msg);
      })
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  if (detail && typeof detail === 'object') {
    const d = detail as { msg?: unknown; message?: unknown };
    if (typeof d.msg === 'string') return d.msg;
    if (typeof d.message === 'string') return d.message;
    return JSON.stringify(detail);
  }
  return `Error ${status}`;
}

/**
 * Error de una respuesta no-2xx. Es un `Error` normal (mismo `message` que
 * antes), con el código HTTP al lado para quien tenga que distinguir un caso
 * — p. ej. el 409 de «alguien cambió esto mientras lo editabas».
 */
export class ApiError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** ¿Es `err` un error de la API con este código HTTP? */
export function isApiStatus(err: unknown, status: number): boolean {
  return err instanceof ApiError && err.status === status;
}

export async function fetchApi<T>(
  url: string,
  opts: RequestInit = {},
): Promise<T> {
  const res = await fetch(withApiBaseUrl(url), {
    ...opts,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Cache-Control': 'no-cache',
      ...opts.headers,
    },
  });
  if (res.status === 401) {
    window.location.href = '/login';
    throw new Error('No autenticado');
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(extractApiError(body, res.status), res.status);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json();
}

/* ── Conexiones a la base de datos (Configuración → Supabase) ── */
export type DbServiceRow = {
  key: string;
  label: string;
  role: string;
  reachable: boolean;
  pool_size?: number;
  max_overflow?: number;
  max_per_process?: number;
  note?: string;
};

/** One tenant web machine, asked by its private address (últimos 5 min en `recent`). */
export type DbMachineRow = {
  machine: string | null;
  role: string | null;
  reachable: boolean;
  error?: string | null;
  pool_size?: number | null;
  in_use?: number | null;
  timeout_s?: number | null;
  recent?: {
    slow_checkout: number;
    pool_timeout: number;
    loop_lag: number;
    admission_rejected: number;
    max_wait_ms: number;
    max_lag_ms: number;
    window_s: number;
  } | null;
  db_on_event_loop?: number | null;
  io_inside_transaction?: number | null;
};

export type DbConnections = {
  max_connections: number | null;
  usage: Record<string, number> | null;
  by_role: Record<string, number> | null;
  pooler: string;
  services: DbServiceRow[];
  machines?: DbMachineRow[];
};

export const getDbConnections = () => fetchApi<DbConnections>('/api/db-connections');

/* ── Discord support cases ── */
export type SupportCaseAttachment = {
  id: number;
  filename: string;
  url: string | null;
  content_type?: string | null;
  is_image: boolean;
};

export type SupportCaseMessage = {
  id: number;
  author_kind: 'client' | 'staff' | 'system';
  author_name?: string | null;
  body: string;
  created_at: string | null;
  attachments?: SupportCaseAttachment[];
};

export type SupportCase = {
  id: number;
  subject: string;
  description?: string | null;
  type: string;
  locale: string;
  status: string;
  priority: string;
  discord_user_id: string;
  discord_username?: string | null;
  discord_thread_id?: string | null;
  discord_thread_url?: string | null;
  workshop_id?: number | null;
  workshop_name?: string | null;
  workshop_slug?: string | null;
  workshop_hint?: string | null;
  created_at: string | null;
  last_message_at?: string | null;
  closed_at?: string | null;
  messages?: SupportCaseMessage[];
};

export const getSupportCases = (params = '') =>
  fetchApi<{ cases: SupportCase[]; total: number }>(`/api/support/cases${params ? `?${params}` : ''}`);

export const getSupportCase = (id: number) =>
  fetchApi<SupportCase>(`/api/support/cases/${id}`);

export const replySupportCase = async (
  id: number,
  body: string,
  file?: File | null,
  options: UploadOptions = {},
): Promise<SupportCaseMessage> => {
  // With a file: through the upload engine, for the progress ring on the
  // attachment. NEVER retried: the reply is posted to the case's Discord
  // thread, and repeating a POST whose answer was lost would post it twice.
  if (file) {
    return uploadToAdmin<SupportCaseMessage>(`/api/support/cases/${id}/reply`, file, MAX_SUPPORT_ATTACHMENT, { ...options, noRetry: true, fields: { body } });
  }
  // Multipart (text only) — don't set Content-Type so the browser adds the
  // multipart boundary.
  const fd = new FormData();
  fd.append('body', body);
  if (file) fd.append('file', file);
  const res = await fetch(withApiBaseUrl(`/api/support/cases/${id}/reply`), {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (res.status === 401) {
    window.location.href = '/login';
    throw new Error('No autenticado');
  }
  if (!res.ok) {
    const b = await res.json().catch(() => ({}));
    throw new Error(extractApiError(b, res.status));
  }
  return res.json();
};

export const patchSupportCase = (
  id: number,
  patch: { status?: string; priority?: string; type?: string; workshop_id?: number | null; workshop_slug?: string | null },
) =>
  fetchApi<SupportCase>(`/api/support/cases/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

export const setupSoporteChannel = () =>
  fetchApi<{
    ok: boolean;
    channels: Record<string, { channel_id: string; button_msg_id: string | null }>;
    staff_roles: string[];
  }>('/api/discord/setup-soporte-channel', { method: 'POST' });

export type SoporteChannelStatus = { configured: boolean; has_channel: boolean; button_msg_id: string | null };
export type SoporteLang = 'es' | 'en' | 'fr' | 'it' | 'de' | 'pt' | 'pl' | 'nl' | 'cs';
export const getSoporteStatus = () =>
  fetchApi<
    { configured: boolean; has_guild: boolean } & Partial<Record<SoporteLang, SoporteChannelStatus>>
  >('/api/discord/soporte-status');

export const deleteSupportCase = (id: number) =>
  fetchApi<{ ok: boolean }>(`/api/support/cases/${id}`, { method: 'DELETE' });

/* ── Shared response shapes ──
 * These describe just the fields the SPA actually consumes — backend
 * returns more, but TypeScript safety is what we need here, not exhaustive
 * mirroring. When the API adds a field UI needs, extend these in place.
 */
/** Access model fields (see documentation/Workshop_Lifecycle.md). */
export type WorkshopAccess = {
  access: string;                 // full | read_only | blocked
  access_reason: string;          // none | payment | manual_hold | billing_exempt | lifecycle
  access_since?: string | null;
  next_transition_at?: string | null;
  access_override: string;        // none | full | blocked | dunning
  access_override_note?: string | null;
  access_override_until?: string | null;
  lifecycle: string;             // active | scheduled_deletion | deleted
  deletion_scheduled_at?: string | null;
};

export type WorkshopRow = WorkshopAccess & {
  id: number;
  name: string;
  slug: string;
  /** Derived reading of the latest subscription (`display_state`), or null. */
  subscription_state: string | null;
  used_storage_bytes: number;
  /** Effective storage cap (GB), resolved from plan ← override. null = unlimited. */
  max_storage_gb: number | null;
  owner_email: string | null;
  owner_name: string | null;
  created_at: string;
  deleted_at?: string | null;
  plan_id: number | null;
  plan_name: string | null;
  user_count: number;
  country: string | null;
  workshop_type: string | null;
  acquisition_source: string | null;
  /** Latest sign-in of any member (null = nobody ever signed in). */
  last_activity_at: string | null;
  /** Orders created in the last 30 days. */
  orders_recent: number;
};

export type WorkshopDetail = WorkshopAccess & {
  id: number;
  name: string;
  slug: string;
  custom_domain: string | null;
  /** Effective storage cap (GB), resolved from plan ← override. null = unlimited. */
  max_storage_gb: number | null;
  used_storage_bytes: number;
  branding: unknown;
  owner_user_id: number | null;
  owner_email: string | null;
  owner_name: string | null;
  created_at: string;
  deleted_at: string | null;
  subscription: { id: number; status: string | null; period_end: string | null } | null;
  /** Per-workshop tier-limit overrides (effective = override ?? plan). */
  entitlement_overrides?: Record<string, boolean | number | null>;
  /** Owner onboarding (first-login setup assistant). null = still pending. */
  onboarding_completed_at?: string | null;
  /** Resumable cursor of the assistant: { step, skipped[], started_at }. */
  onboarding_progress?: { step?: string; skipped?: string[]; started_at?: string };
};

export type ClientMember = {
  user_id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  is_active: boolean;
  role: string;
  joined_at: string | null;
  is_platform_admin?: boolean;
};

export type ClientInvitation = {
  id: number;
  email: string;
  role: string;
  status: string;
  token: string;
  expires_at: string | null;
  created_at: string;
};

export type WorkshopCreatePayload = {
  name: string;
  slug?: string;
  // Either pre-existing user (by id) OR new user (by email/name) — backend
  // accepts both shapes via two distinct sub-flows in the create endpoint.
  owner_user_id?: number;
  owner_email?: string;
  owner_name?: string;
  plan_id?: number | null;
  /** ISO-3166 alpha-2, REQUIRED by the backend. ``'XX'``
   *  (``OTHER_COUNTRY_VALUE``) is the admin-only «no especificado» and stores
   *  NULL — see ``lib/workshopFormOptions.ts``. */
  country?: string;
};

export type WorkshopUpdatePayload = Partial<{
  name: string;
  custom_domain: string | null;
  // Storage cap is edited via entitlement_overrides (key max_storage_gb), not a
  // dedicated field — see the "Límites" tab (LimitesTab).
  entitlement_overrides: Record<string, boolean | number | null>;
  branding: unknown;
  // Workshop profile fields (extended signup migration, 2026-05).
  workshop_type: string;
  acquisition_source: string;
  acquisition_source_other: string;
  tax_id: string;
  country: string;
  city: string;
  province: string;
  postal_code: string;
  address_line1: string;
  address_line2: string;
  // Fiscal data (20260929). Strings: "" clears the column. `holder_type` is
  // `individual` | `company` | "" (sin definir).
  holder_type: string;
  legal_name: string;
  vat_number: string;
  tax_name: string;
  /** `null` SENT explicitly clears it (= sin configurar); absent = untouched. */
  tax_rate: number | null;
  tax_legal_mention: string;
  billing_email: string;
  billing_phone: string;
  billing_website: string;
  payment_instructions: string;
}>;

export type PurgeClientResponse = {
  ok: boolean;
  storage?: { ok: boolean; detail?: string };
};

export type User = {
  id: number;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  username: string | null;
  is_active: boolean;
  created_at?: string;
  client_count?: number;
  avatar_url?: string | null;
  is_platform_admin?: boolean;
  is_superadmin?: boolean;
  has_logged_in?: boolean;
  /** ISO8601 of the last GoTrue sign-in (auth.users.last_sign_in_at); null if never. */
  last_login_at?: string | null;
};

export type UserUpdatePayload = Partial<{
  email: string;
  first_name: string;
  last_name: string;
  username: string;
  is_active: boolean;
  phone: string;
  locale: string;
  marketing_opt_in: boolean;
}>;

/** Polar webhook event row returned by GET /api/polar/events. */
export type PolarEventRow = {
  webhook_id: string;
  event_type: string | null;
  received_at: string | null;
  processed_at: string | null;
  status: string | null;
  error_text: string | null;
  workshop_id: number | null;
  client_name: string | null;
  workshop_slug: string | null;
};

export type PolarEventDetail = PolarEventRow & {
  payload: unknown;
};

/** One row per (user, client) membership returned by GET /api/users/workshop. */
export type WorkshopUserRow = {
  id: number;
  username: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
  avatar_url: string | null;
  has_logged_in: boolean;
  /** ISO8601 of the last GoTrue sign-in (auth.users.last_sign_in_at); null if never. */
  last_login_at?: string | null;
  workshop_id: number;
  workshop_slug: string;
  client_name: string;
  workshop_access: string;
  workshop_country?: string | null;
  workshop_plan_id?: number | null;
  email_verified?: boolean;
  role: string;
  joined_at: string | null;
};

export type Subscription = {
  id: number;
  workshop_id: number;
  status: string;
  interval?: string | null;
  trial_ends_at?: string | null;
  period_start: string | null;
  period_end: string | null;
  polar_subscription_id: string | null;
  // Provider-aware billing channel: a subscription is ONE row with ONE provider at
  // a time. external_subscription_id holds the provider's id (a Polar UUID);
  // amount/currency are what THAT channel charges.
  provider?: string | null;
  external_subscription_id?: string | null;
  amount?: number | null;
  currency?: string | null;
  plan_id: number | null;
  // Set when a cancellation is scheduled (cancel_at_period_end) or terminal.
  cancelled_at?: string | null;
  /** Derived reading, computed by the backend — see lib/subscriptionStatus.ts. */
  display_state?: string | null;
};

export type SubscriptionPayload = Partial<{
  workshop_id: number;
  status: string;
  period_start: string | null;
  period_end: string | null;
  polar_subscription_id: string | null;
  plan_id: number | null;
}>;

export type Plan = {
  id: number;
  name: string;
  price_monthly: number;
  price_annual: number | null;
  /** Storage cap (GB) from entitlements. null = unlimited / unset. */
  max_storage_gb: number | null;
  is_active: boolean;
  /**
   * Shown on the public pricing page, and creatable from the signup. NOT the
   * same as "sellable via Polar" (`polar_product_id`): the free tier and the
   * quote-only one are advertised with no Polar product, while the internal comp
   * plans have no product either and must never be advertised.
   */
  is_public: boolean;
  /**
   * «A medida»: no public price. The pricing API nulls its prices and a signup on
   * it creates a free workshop plus a quote request. Explicit flag, not inferred
   * from "priced and without a Polar product".
   */
  contact_only: boolean;
  polar_product_id: string | null;
  polar_product_id_annual: string | null;
  // Enforcement entitlements (tier limits). English canonical keys; quota
  // values are number | null (null = unlimited). Edited from the Límites tab.
  entitlements?: Record<string, boolean | number | null>;
  created_at?: string;
  updated_at?: string;
};

export type PlanPayload = Partial<Omit<Plan, 'id' | 'created_at' | 'updated_at'>>;

export type Invitation = ClientInvitation & { workshop_id: number; workshop_slug: string; client_name: string };

export type DashboardStats = {
  clients_total: number;
  clients_active: number;
  clients_trial: number;
  users_total: number;
  subscriptions_active: number;
  total_storage_bytes: number;
  mrr_cents: number;
  /** Impago vivo: el proveedor aún reintenta y puede recuperarse solo. */
  workshops_unpaid: number;
  /** Ya bajados al plan gratuito: NO se recuperan sin que el cliente actúe. */
  workshops_downgraded: number;
  workshops_read_only: number;
  workshops_blocked: number;
  workshops_scheduled_deletion: number;
  /** Talleres cuyo propietario aún no ha terminado la configuración inicial. */
  workshops_onboarding_pending: number;
};

/** Rango de las gráficas del panel: última semana por día / últimos 3 meses por semana. */
export type DashboardRange = 'week' | 'quarter';
export type DashboardSeriesPoint = { bucket_start: string; active: number };
export type DashboardTierRow = {
  plan_id: number;
  name: string;
  /** Accounts on the plan, subscription or not. */
  workshops: number;
  /** Free plan: no subscription exists, so active/MRR do not apply. */
  free: boolean;
  active: number;
  trial: number;
  mrr_cents: number;
};
export type DashboardSubscriptions = {
  granularity: 'day' | 'week';
  series: DashboardSeriesPoint[];
  by_tier: DashboardTierRow[];
};
export type DashboardAccountsByPlan = {
  granularity: 'day' | 'week';
  /** Planes públicos, de más barato a más caro. */
  plans: { plan_id: number; name: string }[];
  /** `counts` va indexado por plan_id (como string); `total` es su suma. */
  series: { bucket_start: string; total: number; counts: Record<string, number> }[];
};

/* ── Dashboard ── */
export const getDashboardStats = () => fetchApi<DashboardStats>('/api/dashboard/stats');
export const getDashboardSubscriptions = (range: DashboardRange = 'quarter') =>
  fetchApi<DashboardSubscriptions>(`/api/dashboard/subscriptions?range=${range}`);
export const getDashboardAccountsByPlan = (range: DashboardRange = 'week') =>
  fetchApi<DashboardAccountsByPlan>(`/api/dashboard/accounts-by-plan?range=${range}`);
/** Una máquina cuyo tipo no se pudo deducir, agregada por marca+modelo+protocolo. */
export interface UnidentifiedDevice {
  key: string;
  brand: string | null;
  model: string | null;
  model_raw: string | null;
  protocol: string | null;
  example_serial: string | null;
  /** En cuántos TALLERES aparece. Es lo que ordena el trabajo. */
  workshops: number;
  sightings: number;
  last_seen_at: string | null;
}
/** Una máquina sin tipo que nunca disparó el aviso: no sabemos ni su marca. */
export interface UnsignalledDevice {
  workshop_id: number;
  device_serial: string;
  protocol: string | null;
}
export const getUnidentifiedDevices = (limit = 50) =>
  fetchApi<{
    items: UnidentifiedDevice[];
    sin_aviso: UnsignalledDevice[];
    untyped_machines: number;
    untyped_workshops: number;
  }>(
    `/api/dashboard/unidentified-devices?limit=${limit}`,
  );

/* ── Clients ── */
export type WorkshopListFilters = {
  planId?: number | null;
  access?: string | null;
  deleted?: 'all' | 'exclude' | 'only';
  /**
   * The rest travel as-is under their API names (`country`, `sub_state`,
   * `created_from`…; see `services/workshop_list_filters.py`). Empty values
   * are left out of the query.
   */
  extra?: Record<string, string>;
  // Server-side sort (the list pages in the server, so the order has to too).
  // Only keys the endpoint declares sortable: an unknown `sort_by` is a 422.
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
};
export const getClients = (q = '', limit = 50, offset = 0, filters: WorkshopListFilters = {}) => {
  const sp = new URLSearchParams({ q, limit: String(limit), offset: String(offset) });
  if (filters.sortBy) {
    sp.set('sort_by', filters.sortBy);
    sp.set('sort_dir', filters.sortDir ?? 'asc');
  }
  if (filters.planId != null) sp.set('plan_id', String(filters.planId));
  if (filters.access) sp.set('access', filters.access);
  if (filters.deleted && filters.deleted !== 'all') sp.set('deleted', filters.deleted);
  for (const [k, v] of Object.entries(filters.extra ?? {})) if (v) sp.set(k, v);
  return fetchApi<{ clients: WorkshopRow[]; total: number }>(`/api/workshops?${sp.toString()}`);
};
export const getClient = (id: number) => fetchApi<WorkshopDetail>(`/api/workshops/${id}`);
export const createClient = (data: WorkshopCreatePayload) =>
  fetchApi<WorkshopDetail>('/api/workshops', { method: 'POST', body: JSON.stringify(data) });
export const updateClient = (id: number, data: WorkshopUpdatePayload) =>
  fetchApi<WorkshopDetail>(`/api/workshops/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export type ChangeCurrencyResponse = {
  ok: boolean; currency: string; previous: string | null; entity_updated: boolean; has_entity: boolean;
};
/** «Cambiar moneda»: el tenant cambia antes la moneda de la entidad de facturación. */
export const changeWorkshopCurrency = (id: number, currency: string) =>
  fetchApi<ChangeCurrencyResponse>(`/api/workshops/${id}/currency`, { method: 'POST', body: JSON.stringify({ currency }) });

/** «Reintentar» an e-invoice the provider refused to export (Talleres → ficha → Datos fiscales). */
export const retryWorkshopEInvoice = (workshopId: number, invoiceId: number) =>
  fetchApi<{ ok: boolean; archived: boolean; reason: string | null }>(
    `/api/workshops/${workshopId}/invoices/${invoiceId}/e-invoice/retry`, { method: 'POST', body: '{}' });
export const purgeClient = (id: number, confirmName: string, force = false) =>
  fetchApi<PurgeClientResponse>(
    `/api/workshops/${id}/purge`,
    { method: 'DELETE', body: JSON.stringify({ confirm_name: confirmName, force }) },
  );
export const restoreClient = (id: number) =>
  fetchApi<void>(`/api/workshops/${id}/restore`, { method: 'POST' });
/** Send the owner through the first-login setup assistant again (settings untouched). */
export const resetOnboarding = (id: number) =>
  fetchApi<{ ok: boolean }>(`/api/workshops/${id}/onboarding/reset`, { method: 'POST' });
/** Force the workshop's access regardless of billing. override: none|full|blocked. */
export const setAccessOverride = (
  id: number,
  data: { override: string; note?: string | null; until?: string | null },
) =>
  fetchApi<WorkshopDetail>(`/api/workshops/${id}/access-override`, {
    method: 'POST',
    body: JSON.stringify(data),
  });

/* ── Access / lifecycle policy (Estados y ciclo de vida) ── */
export type LifecyclePolicy = { recovery_days: number };
export type AccessPolicyResponse = {
  lifecycle_policy: LifecyclePolicy;
  /** Plan an unpaid workshop drops to when Polar revokes. `null` = not configured. */
  default_free_plan_id: number | null;
  defaults: { lifecycle_policy: LifecyclePolicy };
};
export const getAccessPolicy = () => fetchApi<AccessPolicyResponse>('/api/access-policy');
export const updateAccessPolicy = (body: {
  lifecycle_policy: LifecyclePolicy;
  default_free_plan_id: number | null;
}) => fetchApi<{ ok: boolean }>('/api/access-policy', { method: 'PUT', body: JSON.stringify(body) });
/* ── Taller por defecto (valores de un taller que no ha puesto los suyos) ── */
export type ExtraBasis = 'unit' | 'plate';
export type WorkshopDefaults = {
  margins: { default_margin: number; threshold_low: number; threshold_high: number };
  order_deadline_days: number;
  quote_validity_days: number;
  materials: { filament: Record<string, number>; resin: Record<string, number> };
  finishes: { procesada: number; pintada: number };
  extras: Record<string, { amount: number; basis: ExtraBasis }>;
  hours: { printers: number; hours_per_day: number; days_per_month: number };
};
export type WorkshopDefaultsResponse = {
  defaults: WorkshopDefaults;
  /** Lo que usaría una instalación nueva: destino de «Restaurar». */
  builtin: WorkshopDefaults;
  /** `false` = no hay fila en BD y se están usando los de fábrica. */
  stored: boolean;
  updated_at: string | null;
  extra_kinds: string[];
};
export const getWorkshopDefaults = () => fetchApi<WorkshopDefaultsResponse>('/api/workshop-defaults');
export const updateWorkshopDefaults = (defaults: WorkshopDefaults) =>
  fetchApi<WorkshopDefaultsResponse>('/api/workshop-defaults', { method: 'PUT', body: JSON.stringify({ defaults }) });
/* ── Países (la lista única de la plataforma: alta, silo, selectores, prefijos,
      identificadores fiscales e impuestos) ── */
export type CountryRegistryResponse = {
  /** La lista efectiva, en orden (normalizada por el backend). */
  countries: CountryEntry[];
  /** Lo que usaría una instalación nueva: destino de «Restaurar». */
  builtin: CountryEntry[];
  /** `false` = no hay fila en BD y se está usando la de fábrica. */
  stored: boolean;
  updated_at: string | null;
  /** Códigos que el backend rechaza siempre (RU, BY). */
  excluded: string[];
  regions: CountryRegion[];
  /** Huella de la fila guardada: la que el PUT debe seguir viendo (409 si no). */
  version: string;
};
export const getCountryRegistry = () => fetchApi<CountryRegistryResponse>('/api/country-registry');
/** Reemplaza la lista entera. `version` es la del GET: si la fila cambió entre
 *  medias (otra pestaña, otro admin) el backend contesta 409 y no escribe. */
export const updateCountryRegistry = (countries: CountryEntry[], version: string) =>
  fetchApi<CountryRegistryResponse>('/api/country-registry', { method: 'PUT', body: JSON.stringify({ countries, version }) });

/* ── Monedas (Plataforma → Países → Monedas; `GET /api/currencies`) ── */
/** `ok` al día · `stale` atrasada · `missing` nunca copiada · `disabled` su
 *  fuente está apagada · `pivot` el euro, que no necesita tipo. */
export type CurrencyState = 'ok' | 'stale' | 'missing' | 'disabled' | 'pivot';

export type CurrencyRow = {
  code: string;
  symbol: string;
  decimals: number;
  /** `ecb` / `bdi`; `null` para el euro. */
  source: 'ecb' | 'bdi' | null;
  source_enabled: boolean;
  /** Países del registro cuya moneda nacional es ésta. */
  countries: string[];
  /** Talleres vivos que trabajan en esta moneda. */
  workshops: number;
  /** 1 EUR = `rate`. */
  rate: number | null;
  rate_date: string | null;
  rate_source: string | null;
  state: CurrencyState;
};

export type FxSourceStatus = {
  source: 'ecb' | 'bdi';
  enabled: boolean;
  latest: string | null;
  expected: string;
  /** Días TARGET de retraso; `null` = nunca se ha guardado nada. */
  behind: number | null;
  stale: boolean;
};

export type CurrenciesResponse = {
  currencies: CurrencyRow[];
  sources: FxSourceStatus[];
  last_run: { status: string; started_at: string | null; finished_at: string | null; error: string | null } | null;
  table_missing: boolean;
  ok: boolean;
  summary: string;
  bdi_enabled: boolean;
  unknown_workshop_currencies: Record<string, number>;
};

export const getCurrencies = () => fetchApi<CurrenciesResponse>('/api/currencies');
export const getClientMembers = (id: number) =>
  fetchApi<ClientMember[] | { members: ClientMember[] }>(`/api/workshops/${id}/members`);
/* ── Users ── */
export const getUsers = (params = '') =>
  fetchApi<{ users: User[]; total: number }>(`/api/users?${params}`);
export const getWorkshopUsers = (params = '') =>
  fetchApi<{ users: WorkshopUserRow[]; total: number }>(`/api/users/workshop?${params}`);
export type UnlinkedIdentity = {
  auth_user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  providers: string[];
  created_at: string | null;
};
export const getOrphanUsers = (params = '') =>
  fetchApi<{ users: User[]; total: number; unlinked_identities?: UnlinkedIdentity[] }>(
    `/api/users/orphans?${params}`,
  );
export const deleteUnlinkedIdentity = (authUid: string) =>
  fetchApi<{ ok: boolean }>(`/api/users/orphans/auth/${encodeURIComponent(authUid)}`, {
    method: 'DELETE',
  });
export const searchUsers = (q: string) =>
  fetchApi<{ items: User[] } | User[]>(
    `/api/users/search?q=${encodeURIComponent(q)}&limit=10`,
  );
export const toggleUserActive = (id: number, data: UserUpdatePayload) =>
  fetchApi<void>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const createUser = (data: UserUpdatePayload) =>
  fetchApi<User>('/api/users', { method: 'POST', body: JSON.stringify(data) });
export const deleteUser = (id: number) =>
  fetchApi<{ ok: boolean; gotrue_deleted?: boolean }>(`/api/users/${id}`, { method: 'DELETE' });
export const getUser = (id: number) =>
  fetchApi<User>(`/api/users/${id}`);
export const updateUser = (id: number, data: UserUpdatePayload) =>
  fetchApi<void>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) });

/* ── Subscriptions ── */
export const getSubscriptions = (params = '') =>
  fetchApi<{ subscriptions: Subscription[]; total: number }>(`/api/subscriptions?${params}`);
export const createSubscription = (data: SubscriptionPayload) =>
  fetchApi<Subscription>('/api/subscriptions', { method: 'POST', body: JSON.stringify(data) });
export const updateSubscription = (id: number, data: SubscriptionPayload) =>
  fetchApi<void>(`/api/subscriptions/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
// Polar-aware lifecycle actions: cancel = at period end (reversible),
// reactivate = undo, revoke = immediate/terminal. The backend mutates Polar.
export const cancelSubscription = (id: number) =>
  fetchApi<Subscription>(`/api/subscriptions/${id}/cancel`, { method: 'POST' });
export const reactivateSubscription = (id: number) =>
  fetchApi<Subscription>(`/api/subscriptions/${id}/reactivate`, { method: 'POST' });
export const revokeSubscription = (id: number) =>
  fetchApi<Subscription>(`/api/subscriptions/${id}/revoke`, { method: 'POST' });

/* ── Subscription discounts + forecast (admin gifts a free/discounted month) ── */
export interface SubscriptionDiscountInfo {
  id: number;
  discount_type: 'percentage' | 'fixed';
  basis_points: number | null;
  amount_cents: number | null;
  currency: string;
  duration: 'once' | 'repeating' | 'forever';
  duration_in_months: number | null;
  starts_period_end: string | null;
  label: string;
  created_at: string | null;
}
export interface ForecastRow {
  cycle: number;
  date: string;
  plan_name: string | null;
  gross: number;
  discount: number;
  charged: number;
  note: string;
}
export interface SubscriptionForecast {
  subscription_id: number;
  currency: string;
  interval: string;
  degraded: boolean;
  scheduled_cancel: boolean;
  terminal: boolean;
  external_discount: boolean;
  active_discount: SubscriptionDiscountInfo | null;
  rows: ForecastRow[];
}
export interface CreateDiscountPayload {
  discount_type: 'percentage' | 'fixed';
  duration: 'once' | 'repeating' | 'forever';
  basis_points?: number;
  amount_eur?: number;
  duration_in_months?: number;
}
export const createSubscriptionDiscount = (id: number, data: CreateDiscountPayload) =>
  fetchApi<SubscriptionDiscountInfo>(`/api/subscriptions/${id}/discount`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
export const removeSubscriptionDiscount = (id: number) =>
  fetchApi<{ ok: boolean }>(`/api/subscriptions/${id}/discount`, { method: 'DELETE' });
export const getSubscriptionForecast = (id: number) =>
  fetchApi<SubscriptionForecast>(`/api/subscriptions/${id}/forecast`);

/* ── Plans ── */
export const getPlans = () => fetchApi<{ plans: Plan[] }>('/api/plans');
export const createPlan = (data: PlanPayload) =>
  fetchApi<Plan>('/api/plans', { method: 'POST', body: JSON.stringify(data) });
export const updatePlan = (id: number, data: PlanPayload) =>
  fetchApi<void>(`/api/plans/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deletePlan = (id: number) =>
  fetchApi<{ ok: true; id: number }>(`/api/plans/${id}`, { method: 'DELETE' });

/* ── Partners (programa de afiliados) ── */
export type Partner = {
  id: number;
  name: string;
  type: string;
  status: string;
  legal_name: string | null;
  tax_id: string | null;
  address: string | null;
  country: string | null;
  phone: string | null;
  representative_name: string | null;
  contact_email: string;
  report_email: string | null;
  iban: string | null;
  /** Owner account — the login that opens the partner portal. Independent from
   *  contact_email (where the invite goes) and report_email (where settlements go). */
  user_id: number | null;
  user_email: string | null;
  user_name: string | null;
  commission_pct: number | null;
  commission_months: number | null;
  notice_months: number | null;
  agreement_duration_months: number | null;
  report_cadence: string;
  agreement_file_url: string | null;
  logo_url: string | null;
  agreement_date: string | null;
  deactivated_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  workshops_count: number | null;
};

export type PartnerCreatePayload = {
  name: string;
  type: string;
  contact_email: string;
  report_email?: string | null;
  legal_name?: string | null;
  tax_id?: string | null;
  address?: string | null;
  country?: string | null;
  phone?: string | null;
  representative_name?: string | null;
  iban?: string | null;
  commission_pct?: number;
  commission_months?: number;
  notice_months?: number;
  agreement_duration_months?: number | null;
  report_cadence?: string;
  agreement_date?: string | null;
  send_invite?: boolean;
};

export type PartnerUpdatePayload = Partial<PartnerCreatePayload> & {
  status?: string;
  agreement_file_url?: string | null;
  logo_url?: string | null;
  /** Owner account. Send a number to link, null to unlink; OMIT to leave it
   *  untouched — the backend only moves the link when this key is present. */
  user_id?: number | null;
};

export const getPartners = (params?: { q?: string; status?: string }) => {
  const qs = new URLSearchParams();
  if (params?.q) qs.set('q', params.q);
  if (params?.status) qs.set('status', params.status);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return fetchApi<{ total: number; partners: Partner[] }>(`/api/partners${suffix}`);
};
export const getPartner = (id: number) => fetchApi<Partner>(`/api/partners/${id}`);
export const createPartner = (data: PartnerCreatePayload) =>
  fetchApi<Partner & { invited: boolean }>('/api/partners', { method: 'POST', body: JSON.stringify(data) });
export const updatePartner = (id: number, data: PartnerUpdatePayload) =>
  fetchApi<Partner>(`/api/partners/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const invitePartner = (id: number) =>
  fetchApi<{ ok: true; sent: boolean }>(`/api/partners/${id}/invite`, { method: 'POST' });
export const deactivatePartner = (id: number) =>
  fetchApi<Partner>(`/api/partners/${id}/deactivate`, { method: 'POST' });
export const reactivatePartner = (id: number) =>
  fetchApi<Partner>(`/api/partners/${id}/reactivate`, { method: 'POST' });

export type PartnerVoucher = {
  id: number;
  partner_id: number;
  code: string;
  polar_discount_id: string | null;
  discount_type: string;
  basis_points: number | null;
  amount_cents: number | null;
  duration: string;
  duration_in_months: number | null;
  max_redemptions: number | null;
  status: string;
  created_at: string | null;
};

export type VoucherCreatePayload = {
  code: string;
  discount_pct: number;
  duration_months?: number | null;
  max_redemptions?: number | null;
};

export const getVouchers = (partnerId: number) =>
  fetchApi<{ vouchers: PartnerVoucher[] }>(`/api/partners/${partnerId}/vouchers`);
export const createVoucher = (partnerId: number, data: VoucherCreatePayload) =>
  fetchApi<PartnerVoucher>(`/api/partners/${partnerId}/vouchers`, { method: 'POST', body: JSON.stringify(data) });
export const disableVoucher = (partnerId: number, voucherId: number) =>
  fetchApi<PartnerVoucher>(`/api/partners/${partnerId}/vouchers/${voucherId}/disable`, { method: 'POST' });

export type PartnerWorkshopRow = {
  link_id: number;
  workshop_id: number;
  workshop_name: string | null;
  workshop_slug: string | null;
  source: string;
  status: string;
  is_new_customer: boolean | null;
  plan_name: string | null;
  interval: string;
  sub_status: string | null;
  /** Lectura canónica (services/subscription_display.py), común a todo proveedor. */
  display_state?: string | null;
  provider?: string | null;
  period_end?: string | null;
  cancelled_at?: string | null;
  in_trial: boolean;
  first_payment_at: string | null;
  commission_months: number;
  months_elapsed: number;
  months_remaining: number;
  period_months: number;
  periods_total: number;
  periods_remaining: number;
  base_ex_tax: number;
  base_provisional: boolean;
  commission_pct: number;
  commission_per_period: number;
  net_commission: number;
  currency: string;
};

export const getPartnerWorkshops = (partnerId: number) =>
  fetchApi<{ workshops: PartnerWorkshopRow[]; total_net_commission: number }>(`/api/partners/${partnerId}/workshops`);

export type PartnerWorkshopPayment = {
  id: number;
  occurred_at: string | null;
  type: 'subscription_payment' | 'refund';
  plan_name: string | null;
  interval: string | null;
  gross_amount: number;
  tax_amount: number;
  net_ex_tax: number;
  list_base_ex_tax: number | null;
  discount_amount: number;
  period_start: string | null;
  period_end: string | null;
  pct: number;
  commissions: boolean;
  commission_amount: number;
};
export const getWorkshopPayments = (partnerId: number, linkId: number) =>
  fetchApi<{ workshop_name: string | null; payments: PartnerWorkshopPayment[] }>(`/api/partners/${partnerId}/workshops/${linkId}/payments`);

export type PartnerLinkCandidate = {
  workshop_id: number;
  name: string;
  slug: string | null;
  linked_partner_id: number | null;
  linked_partner_name: string | null;
  voucher_code: string | null;
  voucher_partner_id: number | null;
};
export const getPartnerLinkCandidates = (partnerId: number, q: string) =>
  fetchApi<{ candidates: PartnerLinkCandidate[] }>(`/api/partners/${partnerId}/link-candidates?q=${encodeURIComponent(q)}`);

export const linkWorkshop = (partnerId: number, workshopId: number) =>
  fetchApi<PartnerWorkshopRow>(`/api/partners/${partnerId}/workshops`, { method: 'POST', body: JSON.stringify({ workshop_id: workshopId }) });
export const voidWorkshopLink = (partnerId: number, linkId: number) =>
  fetchApi<PartnerWorkshopRow>(`/api/partners/${partnerId}/workshops/${linkId}/void`, { method: 'POST' });
export const unlinkWorkshop = (partnerId: number, linkId: number) =>
  fetchApi<{ ok: true }>(`/api/partners/${partnerId}/workshops/${linkId}`, { method: 'DELETE' });

export type PartnerReportBreakdownRow = {
  workshop_name: string;
  plan_name: string;
  interval: string | null;
  charges: number;
  refunds: number;
  base: number;
  pct: number;
  gross: number;
  reg: number;
  net: number;
};
export type PartnerReportRow = {
  id: number;
  partner_id: number;
  period_type: string;
  period_label: string;
  period_start: string | null;
  period_end: string | null;
  total_base: number;
  total_commission: number;
  regularizations_total: number;
  total_net: number;
  workshops_count: number;
  status: string;
  invoice_status: string;
  has_pdf: boolean;
  invoice_uploaded: boolean;
  invoice_uploaded_at: string | null;
  invoice_filename: string | null;
  generated_at: string | null;
  sent_at: string | null;
  rows: PartnerReportBreakdownRow[];
};

export type PartnerReportPeriod = {
  period_start: string;
  label: string;
  already_generated: boolean;
};
export const getPartnerReports = (partnerId: number) =>
  fetchApi<{ reports: PartnerReportRow[] }>(`/api/partners/${partnerId}/reports`);
export const getPartnerReportPeriods = (partnerId: number) =>
  fetchApi<{ period_type: string; periods: PartnerReportPeriod[] }>(`/api/partners/${partnerId}/reports/periods`);
export const generatePartnerReport = (partnerId: number, periodStart?: string | null) =>
  fetchApi<PartnerReportRow>(`/api/partners/${partnerId}/reports/generate`, { method: 'POST', body: JSON.stringify({ period_start: periodStart ?? null }) });
export const sendPartnerReport = (partnerId: number, reportId: number) =>
  fetchApi<PartnerReportRow>(`/api/partners/${partnerId}/reports/${reportId}/send`, { method: 'POST' });
export const deletePartnerReport = (partnerId: number, reportId: number) =>
  fetchApi<{ ok: true }>(`/api/partners/${partnerId}/reports/${reportId}`, { method: 'DELETE' });
export const updatePartnerReport = (partnerId: number, reportId: number, invoiceStatus: string) =>
  fetchApi<PartnerReportRow>(`/api/partners/${partnerId}/reports/${reportId}`, { method: 'PATCH', body: JSON.stringify({ invoice_status: invoiceStatus }) });
export const partnerReportPdfUrl = (partnerId: number, reportId: number) => `/api/partners/${partnerId}/reports/${reportId}/pdf`;
export const partnerReportInvoiceUrl = (partnerId: number, reportId: number) => `/api/partners/${partnerId}/reports/${reportId}/invoice`;
export const deletePartnerReportInvoice = (partnerId: number, reportId: number) =>
  fetchApi<PartnerReportRow>(`/api/partners/${partnerId}/reports/${reportId}/invoice`, { method: 'DELETE' });

export type PartnerStats = {
  partners_active: number;
  workshops_linked: number;
  invoices_pending: number;
  payments_pending: { count: number; amount: number };
};
export const getPartnerStats = () => fetchApi<PartnerStats>('/api/partners/stats');

export const uploadPartnerAgreement = (partnerId: number, file: File, options: UploadOptions = {}): Promise<Partner> =>
  uploadToAdmin<Partner>(`/api/partners/${partnerId}/agreement`, file, MAX_PARTNER_PDF, options);
export const deletePartnerAgreement = (partnerId: number) =>
  fetchApi<Partner>(`/api/partners/${partnerId}/agreement`, { method: 'DELETE' });
export const partnerAgreementUrl = (partnerId: number) => `/api/partners/${partnerId}/agreement`;

/* ── Límites (tier entitlements) ── */
export type LimitSpec = {
  key: string;
  kind: 'feature' | 'quota';
  group: string;
  label: string;
};
export type LimitsPlanRow = {
  id: number;
  name: string;
  price_monthly: number;
  is_active: boolean;
  is_sellable: boolean;
  /** Storage cap (GB) from entitlements (also present inside `entitlements`). */
  max_storage_gb: number | null;
  entitlements: Record<string, boolean | number | null>;
  active_clients: number;
};
export type LimitsUpdatePayload = {
  // Storage is a normal quota inside `entitlements` now (no dedicated field).
  plans: { id: number; entitlements: Record<string, boolean | number | null> }[];
};
export const getLimits = () =>
  fetchApi<{ specs: LimitSpec[]; plans: LimitsPlanRow[] }>('/api/plans/limits');
export const updateLimits = (data: LimitsUpdatePayload) =>
  fetchApi<{ ok: true; changed: number }>('/api/plans/limits', {
    method: 'PUT',
    body: JSON.stringify(data),
  });

/* ── Polar ── */
export type PolarProduct = {
  id: string;
  name: string;
  price_label: string;
  recurring_interval: string | null;
  trial_label: string | null;
  is_archived: boolean;
};
export const getPolarProducts = () =>
  fetchApi<{ products: PolarProduct[] }>('/api/polar/products');
/* ── Invitations ── */
export const getInvitations = (params = '') =>
  fetchApi<{ invitations: Invitation[]; total: number }>(`/api/invitations?${params}`);
export const revokeInvitation = (workshopId: number, invitationId: number) =>
  fetchApi<void>(`/api/workshops/${workshopId}/invitations/${invitationId}`, { method: 'DELETE' });

/* ── Stats (Estadísticas) ──
 * Customers' workshops only: the plan «Desarrollo», the Test Runner's and
 * deleted ones are out (services/usage_stats.py). */
export type StatsPeriod = '7d' | '30d' | '12m';
export type StatsGranularity = 'week' | 'month' | 'year';
export type StatsEntity =
  | 'workshops' | 'users' | 'designs' | 'pieces' | 'products' | 'variants'
  | 'orders' | 'order_items' | 'customers' | 'quotes' | 'invoices' | 'credit_notes'
  | 'printers' | 'print_jobs' | 'filaments' | 'resins';
export type StatsGrowthRow = {
  key: StatsEntity;
  label: string;
  group: string;
  /** Created in the period, in the period before it (same length) and ever. */
  current: number;
  previous: number;
  total: number;
  /** One number per bucket of `StatsUsage.buckets`. */
  series: number[];
};
export type StatsUsage = {
  period: StatsPeriod;
  unit: 'day' | 'month';
  /** ISO date of each bucket's start (UTC). */
  buckets: string[];
  excluded: { development_workshops: number };
  /** Workshops that created something (not counting joining or being created). */
  active: { current: number; previous: number; series: number[] };
  growth: StatsGrowthRow[];
  /** Share of the period's active workshops that used each part. */
  adoption: { label: string; workshops: number; pct: number }[];
  quote_funnel: { created: number; sent: number; accepted: number; ordered: number };
  orders_by_origin: { origins: string[]; series: Record<string, number>[] };
  printers_by_route: { route: 'agent' | 'cloud' | 'both' | 'none'; fdm: number; resin: number; cnc: number; unknown: number }[];
};
export type StatsGrowth = {
  entity: StatsEntity;
  label: string;
  granularity: StatsGranularity;
  points: { bucket: string; created: number; cumulative: number }[];
};
export type StatsRatioGroup = { label: string; one: number; two: number; more: number; average: number | null };
export type StatsRatios = {
  kind: 'design' | 'product';
  by: 'month' | 'age';
  total: number;
  /** Buckets 0 · 1 · 2 · 3–5 · 6+ over every design/product. */
  histogram: { label: string; count: number }[];
  /** Children per parent among those with at least one; `_recent` = created in the last 90 days. */
  average: number | null;
  average_recent: number | null;
  /** Per month of creation (ISO dates) or per workshop age («< 1 mes»…). */
  groups: StatsRatioGroup[];
};
export type StatsWorkshops = {
  excluded: { development_workshops: number };
  workshops: number;
  users: number;
  onboarded: number;
  active_30d: number;
  plans: { name: string; count: number }[];
  members: { label: string; count: number }[];
  countries: ({ country: string } & Record<'hobby' | 'particular' | 'negocio' | 'estudio' | 'educacion' | 'unknown', number>)[];
  sources: { source: string; count: number }[];
};
export type SentryIssue = {
  id: string;
  shortId: string | null;
  title: string;
  level: string | null;
  /** `unresolved` | `resolved` | `ignored` (Sentry's name for «archivado»). */
  status: string | null;
  project: string | null;
  count: string | number | null;
  user_count: number | null;
  last_seen: string | null;
  permalink: string | null;
};

/** `total` counts every match; `issues` stops at `limit`. `link` is the same search in Sentry. */
export const getSentryIssues = (params = '') =>
  fetchApi<{ total: number; issues: SentryIssue[]; org_link: string; link: string }>(`/api/sentry/issues?${params}`);
export const getStatsUsage = (period: StatsPeriod) =>
  fetchApi<StatsUsage>(`/api/stats/usage?period=${period}`);
export const getStatsGrowth = (entity: StatsEntity, granularity: StatsGranularity) =>
  fetchApi<StatsGrowth>(`/api/stats/growth?entity=${entity}&granularity=${granularity}`);
export const getStatsRatios = (kind: StatsRatios['kind'], by: StatsRatios['by']) =>
  fetchApi<StatsRatios>(`/api/stats/ratios?kind=${kind}&by=${by}`);
export const getStatsWorkshops = () => fetchApi<StatsWorkshops>('/api/stats/workshops');

/* ── Storage browser ── */
export type StorageTenantUsage = {
  slug: string;
  usedBytes: number;
  usedHuman: string;
  maxGb: number;
};
export type StorageStats = {
  bucketTotal: number;
  bucketTotalHuman: string;
  tenantsTotal: number;
  tenantsTotalHuman: string;
  tenants: StorageTenantUsage[];
  lastRecalc: string | null;
};
export type StorageObject = {
  key: string;
  name: string;
  size: number;
  sizeHuman: string;
  lastModified: string | null;
};
export type StorageBrowseResult = {
  prefix: string;
  prefixes: string[];
  objects: StorageObject[];
  totalSize: number;
  totalSizeHuman: string;
  error?: string;
};
export type CleanupOrphanItem = {
  key: string;
  slug?: string;
  category?: string;
  size?: number;
  sizeHuman: string;
  lastModified?: string | null;
};
export type CleanupOrphansResult = {
  count: number;
  freedBytes?: number;
  freedHuman: string;
  dryRun: boolean;
  orphans: CleanupOrphanItem[];
  skippedRecent?: number;
  /** Talleres ANALIZADOS (denominador del barrido), no los afectados. */
  scannedTenants?: number;
  /** Talleres de los que se borró algo. Solo en una ejecución real. */
  affectedTenants?: number;
  /** Huella por slug del conjunto analizado; se reenvía en el borrado real (guardia TOCTOU). */
  tokens?: Record<string, string>;
  /** Objetos que S3 rechazó borrar en una ejecución real (la cuota no se tocó por ellos). */
  failed?: number;
  errors?: { slug: string; error: string }[];
  error?: string;
  /** El borrado ocurrió pero no se pudo escribir en platform_events. */
  auditFailed?: boolean;
};
export const getStorageStats = () => fetchApi<StorageStats>('/api/storage/stats');
export const getStorageBrowse = (prefix = '') =>
  fetchApi<StorageBrowseResult>(`/api/storage/browse?prefix=${encodeURIComponent(prefix)}`);
// On a real run (dryRun=false) the caller passes the per-slug ``tokens`` from the
// dry-run it showed the operator; the backend forwards them so the tenant refuses
// (409) any tenant whose orphan set changed since review (TOCTOU guard).
export const cleanupOrphans = (dryRun = true, approvedTokens?: Record<string, string>) =>
  fetchApi<CleanupOrphansResult>(`/api/storage/cleanup-orphans?dry_run=${dryRun}`, {
    method: 'POST',
    ...(dryRun ? {} : { body: JSON.stringify({ approved_tokens: approvedTokens ?? {} }) }),
  });
export const recalculateStorage = () =>
  fetchApi<{ ok: boolean }>('/api/storage/recalculate', { method: 'POST' });

/* ── Translations ── */
export type TranslationRow = { key: string; value: string };
export type AiTranslateResponse = { translations: TranslationRow[] };
export type AddLanguageResponse = {
  ok: boolean;
  language_code: string;
  language_name: string;
  translations: Record<string, string>;
  total: number;
  email_templates_translated: number;
};

export const aiTranslate = (data: { keys: Array<{ key: string; source_value: string }>; target_language_code: string; target_language_name: string }) =>
  fetchApi<AiTranslateResponse>('/api/translations/ai-translate', { method: 'POST', body: JSON.stringify(data) });
export const addLanguage = (data: { language_code: string; language_name: string; source: Record<string, string> }) =>
  fetchApi<AddLanguageResponse>('/api/translations/add-language', { method: 'POST', body: JSON.stringify(data) });
/** Returns a Blob suitable for an anchor-tag download. Bypasses fetchApi's
 *  JSON contract since the response is application/zip. */
export async function exportTranslationsZip(locales: Record<string, Record<string, string>>): Promise<Blob> {
  const res = await fetch(withApiBaseUrl('/api/translations/export-zip'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Bolloprint-Admin': '1' },
    body: JSON.stringify({ locales }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error((err as { detail?: string }).detail || 'Error generando ZIP');
  }
  return res.blob();
}

/* ── Email templates ── */
export type EmailTemplateLang = { language_code: string; subject: string; body_text: string; body_html: string | null };
export type EmailTemplate = {
  key: string;
  section: string;
  label: string;
  description: string;
  variables: Record<string, string>;
  languages: EmailTemplateLang[];
};

export const listEmailTemplates = () =>
  fetchApi<{ templates: EmailTemplate[] }>('/api/email-templates');
export const getEmailTemplate = (key: string, lang: string) =>
  fetchApi<EmailTemplateLang & { exists: boolean }>(`/api/email-templates/${key}/${lang}`);
export const saveEmailTemplate = (
  key: string,
  lang: string,
  body: { subject: string; body_text: string; body_html: string | null },
) =>
  fetchApi<{ ok: boolean }>(`/api/email-templates/${key}/${lang}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
export const deleteEmailTemplate = (key: string, lang: string) =>
  fetchApi<{ ok: boolean }>(`/api/email-templates/${key}/${lang}`, { method: 'DELETE' });
export const sendTestEmail = (key: string, lang: string) =>
  fetchApi<{ ok: boolean; sent_to: string }>(
    `/api/email-templates/${key}/test?language_code=${encodeURIComponent(lang)}`,
    { method: 'POST' },
  );
export const regenerateEmailTemplate = (key: string, lang: string) =>
  fetchApi<{
    ok: boolean;
    subject: string;
    body_text: string;
    body_html: string | null;
  }>(`/api/email-templates/${key}/${lang}/regenerate`, { method: 'POST' });
export const previewEmailTemplate = (
  key: string,
  body: { language_code: string; body_html: string },
) =>
  fetchApi<{ html: string }>(`/api/email-templates/${key}/preview`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
export async function uploadEmailTemplateImage(file: File): Promise<{ url: string; filename: string }> {
  if (file.size > MAX_EMAIL_IMAGE) throw new Error(tooLargeMessage(MAX_EMAIL_IMAGE));
  const fd = new FormData();
  fd.append('file', file);
  const res = await safeUploadFetch(withApiBaseUrl('/api/email-templates/upload-image'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Bolloprint-Admin': '1' },
    body: fd,
  }, file);
  if (!res.ok) {
    throw new Error(extractApiError(await res.json().catch(() => ({})), res.status) || 'Error al subir imagen');
  }
  return res.json();
}

/* ── AI providers ── */
export type AiProvider = 'gemini' | 'claude';
export type AiProviderTestResult = { ok: boolean; error: string | null; checked_at: string | null };
export const testAiProvider = (provider: AiProvider) =>
  fetchApi<AiProviderTestResult>(`/api/ai/test/${provider}`, { method: 'POST' });
export const setAiDefaultProvider = (provider: AiProvider) =>
  fetchApi<{ ok: boolean; default: AiProvider }>('/api/ai/set-default', {
    method: 'POST',
    body: JSON.stringify({ provider }),
  });
export const unconfigureAiProvider = (provider: AiProvider) =>
  fetchApi<{ ok: boolean; default: AiProvider | null }>(`/api/ai/unconfigure/${provider}`, {
    method: 'POST',
  });
export const getAiHealth = () =>
  fetchApi<{ providers: Record<string, { ok: boolean; checked_at: string | null; error: string | null }> }>(
    '/api/ai/health',
  );

/* ── Platform settings ── */
export type PlatformSetting = {
  key: string;
  value: string;
  isSecret?: boolean;
  category?: string;
  label?: string;
  hasValue?: boolean;
  updatedAt?: string | null;
  /** Nombre de la variable de entorno de arranque, si la clave tiene una. */
  envVar?: string | null;
  envValuePresent?: boolean;
  /** De dónde sale el valor efectivo: la fila de BD gana; si no, el env. */
  effectiveSource?: 'database' | 'env' | null;
};
export type PlatformSettingsResponse = { settings: PlatformSetting[] };
export type HealthCheck = { ok: boolean; label: string; detail?: string };
export type HealthStatus = Record<string, HealthCheck>;
export const getPlatformSettings = () =>
  fetchApi<PlatformSettingsResponse | PlatformSetting[]>('/api/platform-settings');
export const getHealthStatus = () => fetchApi<HealthStatus>('/api/platform-settings/health');
export const updateSetting = (key: string, value: string) =>
  fetchApi<void>(`/api/platform-settings/${key}`, { method: 'POST', body: JSON.stringify({ value }) });
/** Borra la fila (el sistema vuelve al env o al default). 403 en claves de sólo lectura. */
export const deleteSetting = (key: string) =>
  fetchApi<{ ok: boolean }>(`/api/platform-settings/${key}`, { method: 'DELETE' });
export const rotateAppEncryptionKey = () =>
  fetchApi<{ ok: boolean; rows_reencrypted: number }>('/api/platform-settings/app-encryption-key/rotate', { method: 'POST' });

/* ── Recurring tasks ── */
export type ScheduledTaskRun = {
  status: 'success' | 'error' | 'running';
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  detail: string | null;
  error: string | null;
};
export type ScheduledTask = {
  key: string;
  name: string;
  description: string;
  container: 'tenant' | 'admin';
  schedule: string;
  interval_seconds: number;
  category: 'maintenance' | 'realtime';
  monitored: boolean;
  last_run: ScheduledTaskRun | null;
};
export type ScheduledTasksResponse = { tasks: ScheduledTask[] };
export const getScheduledTasks = () =>
  fetchApi<ScheduledTasksResponse>('/api/platform/scheduled-tasks');

export const runScheduledTaskNow = (taskKey: string) =>
  fetchApi<{ ok: boolean; task_key: string; status: string }>(
    `/api/platform/scheduled-tasks/${encodeURIComponent(taskKey)}/run-now`,
    { method: 'POST' },
  );

/**
 * Ejecuta la tarea SIN escribir y devuelve lo que habría hecho. Solo la
 * admiten las que están en `_ADMIN_DRY_RUNNERS` (hoy `marketing_list_sync`);
 * el resto contesta 422. Corre en la propia petición, así que la respuesta ya
 * trae el resultado en `detail`.
 */
export const simulateScheduledTask = (taskKey: string) =>
  fetchApi<{ ok: boolean; task_key: string; status: string; dry_run: boolean; detail: string }>(
    `/api/platform/scheduled-tasks/${encodeURIComponent(taskKey)}/run-now?dry_run=true`,
    { method: 'POST' },
  );

/* ── Listmonk: las listas de correo, sólo lectura ── */
export type NewsletterList = {
  id: number;
  locale: string;
  name: string;
  /** 'single' | 'double', tal cual lo dice listmonk. */
  optin: string;
  /** null si no se pudo leer el detalle; nunca significa cero. */
  subscribers: number | null;
};
export type NewsletterFamily = {
  family: string;
  label: string;
  lists: NewsletterList[];
  /** Idiomas de la plataforma sin lista creada en esta familia. */
  missing: string[];
};
/** `enabled: false` NO es un error: en dev es el estado normal y `reason` lo explica. */
export type NewsletterListsResponse = {
  enabled: boolean;
  reason: string | null;
  families: NewsletterFamily[];
};
export const getNewsletterLists = (refresh = false) =>
  fetchApi<NewsletterListsResponse>(`/api/listmonk/lists${refresh ? '?refresh=true' : ''}`);

/** Una persona, tal como la ve el Admin. `status` es GLOBAL; el de cada lista va dentro. */
export type NewsletterSubscriber = {
  id: number;
  email: string;
  name: string | null;
  /** 'enabled' | 'blocklisted' — la lista negra es de la persona, no de una lista. */
  status: string;
  created_at: string | null;
  lists: { id: number; name: string | null; status: string | null }[];
  /** Solo en la ficha. `null` = no se pudieron leer, que no es lo mismo que ninguno. */
  bounces?: { type: string; source: string; created_at: string }[] | null;
};
export type NewsletterSubscribersResponse = {
  enabled: boolean;
  reason: string | null;
  items: NewsletterSubscriber[];
  total: number;
};

export const searchNewsletterSubscribers = (
  params: { q?: string; listId?: number | null; page: number; perPage: number },
) => {
  const qs = new URLSearchParams({ page: String(params.page), per_page: String(params.perPage) });
  if (params.q) qs.set('q', params.q);
  if (params.listId) qs.set('list_id', String(params.listId));
  return fetchApi<NewsletterSubscribersResponse>(`/api/listmonk/subscribers?${qs}`);
};
export const getNewsletterSubscriber = (id: number) =>
  fetchApi<NewsletterSubscriber>(`/api/listmonk/subscribers/${id}`);
export const addNewsletterSubscriber = (body: { email: string; list_id: number; name?: string }) =>
  fetchApi<{ ok: boolean }>('/api/listmonk/subscribers', { method: 'POST', body: JSON.stringify(body) });
/** Deja constancia: el barrido la respeta para siempre. Es la respuesta a una protesta. */
export const unsubscribeNewsletterSubscriber = (id: number, listIds: number[]) =>
  fetchApi<{ ok: boolean }>(`/api/listmonk/subscribers/${id}/unsubscribe`,
    { method: 'POST', body: JSON.stringify({ list_ids: listIds }) });
/** NO deja constancia: la persona puede volver. Sólo para deshacer un error nuestro. */
export const detachNewsletterSubscriber = (id: number, listIds: number[]) =>
  fetchApi<{ ok: boolean }>(`/api/listmonk/subscribers/${id}/detach`,
    { method: 'POST', body: JSON.stringify({ list_ids: listIds }) });
export const setNewsletterBlocklist = (id: number, blocked: boolean) =>
  fetchApi<{ ok: boolean }>(`/api/listmonk/subscribers/${id}/${blocked ? 'blocklist' : 'unblocklist'}`,
    { method: 'POST', body: JSON.stringify({}) });
export const deleteNewsletterSubscriber = (id: number) =>
  fetchApi<{ ok: boolean }>(`/api/listmonk/subscribers/${id}`, { method: 'DELETE' });
export const exportNewsletterSubscriber = (id: number) =>
  fetchApi<unknown>(`/api/listmonk/subscribers/${id}/export`);

/* ── Workers (web/worker split control plane) ── */
export type WorkerInstance = {
  worker_id: string;
  region: string | null;
  app_role: string | null;
  status: string;
  health: 'online' | 'stale' | 'offline';
  version: string | null;
  capacity_workshops: number | null;
  capacity_printers: number | null;
  cpu_cores: number | null;
  cpu_percent: number | null;
  mem_mb: number | null;
  mem_limit_mb: number | null;
  assigned_count: number;
  last_seen_at: string | null;
  started_at: string | null;
  age_seconds: number | null;
};
export type WorkshopAssignmentRow = {
  workshop_id: number;
  slug: string | null;
  name: string | null;
  current_worker_id: string | null;
  desired_worker_id: string | null;
  state: string;
  lease_ok: boolean;
  printer_count: number;
  last_error: string | null;
  converging: boolean;
  updated_at: string | null;
};
export type GlobalLeaseRow = {
  lease_name: string;
  holder_worker_id: string | null;
  desired_worker_id: string | null;
  lease_ok: boolean;
  converging: boolean;
};
export type WorkersResponse = {
  workers: WorkerInstance[];
  assignments: WorkshopAssignmentRow[];
  global_leases: GlobalLeaseRow[];
};
export const getWorkers = () => fetchApi<WorkersResponse>('/api/platform/workers');

/* ── Marketplaces (Procesos → Marketplaces, ficha → Conexiones) ── */
export type MarketplaceJobKind = 'orders' | 'listings' | 'ship_audit';
export type MarketplaceJobsRow = {
  marketplace: string;
  kind: MarketplaceJobKind;
  accounts: number;
  due: number;
  late: number;
  running: number;
  failing: number;
  oldest_due_at: string | null;
  last_finished_at: string | null;
};
export type MarketplaceQueueRow = {
  queue: 'inbox' | 'outbox';
  marketplace: string;
  pending: number;
  retrying: number;
  exhausted: number;
  oldest_pending_at: string | null;
  done_24h: number;
};
export type MarketplaceRunnerRow = {
  runner: string;
  jobs_1h: number;
  failed_1h: number;
  inbox_24h: number;
  outbox_24h: number;
};
export type MarketplaceFailureRow = {
  source: 'job' | 'inbox' | 'outbox';
  workshop_id: number;
  workshop_slug: string | null;
  marketplace: string;
  what: string;
  error: string | null;
  runner: string | null;
  at: string | null;
};
export type MarketplaceAccountJob = {
  kind: MarketplaceJobKind;
  due_at: string | null;
  last_started_at: string | null;
  last_finished_at: string | null;
  last_outcome: 'ok' | 'failed' | 'skipped' | null;
  last_error: string | null;
  last_runner: string | null;
  last_duration_ms: number | null;
  attempts: number;
  running: boolean;
  lease_owner: string | null;
};
export type MarketplaceAccountActivity = {
  account_id: number;
  marketplace: string;
  shop_name: string | null;
  shop_id: string | null;
  status: string;
  last_sync_at: string | null;
  writeback_enabled: boolean;
  jobs: MarketplaceAccountJob[];
};
export type MarketplaceActivity = {
  clock: {
    /** The global lease of the marketplace clock (frozen name). Absent from an older admin. */
    lease_name?: string;
    holder_worker_id: string | null;
    desired_worker_id: string | null;
    lease_ok: boolean;
    lease_expires_at: string | null;
  };
  /** One per channel with an app quota. `label` is absent from an older admin. */
  calls_today: { marketplace: string; label?: string; calls: number | null; daily_budget: number | null }[];
  jobs: MarketplaceJobsRow[];
  queues: MarketplaceQueueRow[];
  runners: MarketplaceRunnerRow[];
  failures: MarketplaceFailureRow[];
  late_minutes: number;
  /** Only with `workshopId`. */
  accounts?: MarketplaceAccountActivity[];
};
export const getMarketplaceActivity = (workshopId?: number) =>
  fetchApi<MarketplaceActivity>(
    `/api/platform/marketplaces/activity${workshopId ? `?workshop_id=${workshopId}` : ''}`,
  );
/** One marketplace channel as its manifest declares it
 *  (`bolloprint_shared.marketplace_channels`), for the screens that derive
 *  from it. Settings come WITHOUT values: those are `getPlatformSettings`. */
export type MarketplaceChannelSetting = {
  key: string;
  label: string;
  secret: boolean;
  required: boolean;
  env_var: string | null;
};
export type MarketplaceChannel = {
  key: string;
  label: string;
  stage: 'live' | 'beta' | 'dev_only';
  logo: string | null;
  site: string | null;
  feature: string;
  capabilities: string[];
  auth_kind: string;
  help: string[];
  settings: MarketplaceChannelSetting[];
  quota: { per_second: number; provider_per_second: number | null; per_day: number | null } | null;
  tasks: { kind: MarketplaceJobKind; key: string; interval_seconds: number }[];
  webhooks: boolean;
};
export const getMarketplaceChannels = () =>
  fetchApi<{ channels: MarketplaceChannel[] }>('/api/platform/marketplace-channels');
export const moveWorkshop = (workshopId: number, workerId: string | null) =>
  fetchApi<{ ok: boolean; desired_worker_id: string | null }>(
    `/api/platform/workshops/${workshopId}/move`,
    { method: 'POST', body: JSON.stringify({ worker_id: workerId }) },
  );
export const moveGlobalLease = (leaseName: string, workerId: string | null) =>
  fetchApi<{ ok: boolean; desired_worker_id: string | null }>(
    `/api/platform/global-leases/${encodeURIComponent(leaseName)}/move`,
    { method: 'POST', body: JSON.stringify({ worker_id: workerId }) },
  );
export const pauseWorkshop = (workshopId: number) =>
  fetchApi<{ ok: boolean }>(`/api/platform/workshops/${workshopId}/pause`, { method: 'POST' });
export const resumeWorkshop = (workshopId: number) =>
  fetchApi<{ ok: boolean }>(`/api/platform/workshops/${workshopId}/resume`, { method: 'POST' });

/* ── Platform admins ── */
export const removePlatformAdmin = (id: number) =>
  fetchApi<void>(`/api/platform-admins/${id}`, { method: 'DELETE' });

/* ── Auth ── */
export type AuthOrganization = {
  id: number;
  slug: string;
  name: string;
  role?: string;
};
export const getMyOrganizations = () => fetchApi<AuthOrganization[]>('/api/auth/organizations');
/* ── Dev (Desarrollo) — internal admin kanban + roadmap ── */

export type DevColumn = {
  id: number;
  title: string;
  position: number;
  column_type: string;
  is_inbox?: boolean;
  color: string | null;
  created_at?: string;
};

export type DevColumnPayload = Partial<{
  title: string;
  position: number;
  column_type: string;
  is_inbox: boolean;
  color: string | null;
}>;

export type DevVersion = {
  id: number;
  version: string;
  estimated_date: string | null;
  release_date?: string | null;
  status: string;
  quarter: number | null;
  quarter_year: number | null;
  notes: string | null;
  created_at?: string;
  // Server-aggregated counts surfaced to the UI roadmap:
  task_count?: number;
  tasks_completed?: number;
};

export type DevVersionPayload = Partial<Omit<DevVersion, 'id' | 'created_at'>>;

export type DevAdminUser = {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url?: string | null;
};

export type DevTask = {
  id: number;
  column_id: number | null;
  version_id: number | null;
  title: string;
  description: string | null;
  type: string;
  status: string;
  priority: string;
  assigned_to: number | null;
  position: number;
  due_date: string | null;
  comments?: unknown;
  created_at?: string;
  updated_at?: string;
  // Server-joined fields surfaced to the UI:
  assigned_user?: DevAdminUser | null;
  version_name?: string | null;
  attachments?: DevTaskAttachment[];
};

export type DevTaskPayload = Partial<{
  column_id: number | null;
  version_id: number | null;
  title: string;
  description: string | null;
  type: string;
  status: string;
  priority: string;
  assigned_to: number | null;
  position: number;
  due_date: string | null;
  comments: unknown;
}>;

export type DevTaskAttachment = {
  id: number;
  task_id: number;
  filename: string;
  s3_key?: string;
  size: number;
  content_type: string | null;
  uploaded_at?: string;
};

// Columns
export const getDevColumns = () =>
  fetchApi<{ columns: DevColumn[] }>('/api/dev/columns');
export const createDevColumn = (data: DevColumnPayload) =>
  fetchApi<DevColumn>('/api/dev/columns', { method: 'POST', body: JSON.stringify(data) });
export const updateDevColumn = (id: number, data: DevColumnPayload) =>
  fetchApi<DevColumn>(`/api/dev/columns/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteDevColumn = (id: number) =>
  fetchApi<void>(`/api/dev/columns/${id}`, { method: 'DELETE' });
export const reorderDevColumns = (order: number[]) =>
  fetchApi<void>('/api/dev/columns/reorder', { method: 'PATCH', body: JSON.stringify({ order }) });

// Versions
export const getDevVersions = () =>
  fetchApi<{ versions: DevVersion[] }>('/api/dev/versions');
export const createDevVersion = (data: DevVersionPayload) =>
  fetchApi<DevVersion>('/api/dev/versions', { method: 'POST', body: JSON.stringify(data) });
export const updateDevVersion = (id: number, data: DevVersionPayload) =>
  fetchApi<DevVersion>(`/api/dev/versions/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteDevVersion = (id: number) =>
  fetchApi<void>(`/api/dev/versions/${id}`, { method: 'DELETE' });

// Tasks
export const getDevTasks = (params = '') =>
  fetchApi<{ tasks: DevTask[] }>(`/api/dev/tasks${params ? '?' + params : ''}`);
export const createDevTask = (data: DevTaskPayload) =>
  fetchApi<DevTask>('/api/dev/tasks', { method: 'POST', body: JSON.stringify(data) });
export const updateDevTask = (id: number, data: DevTaskPayload) =>
  fetchApi<DevTask>(`/api/dev/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteDevTask = (id: number) =>
  fetchApi<void>(`/api/dev/tasks/${id}`, { method: 'DELETE' });
export const moveDevTask = (id: number, data: { column_id: number; position: number }) =>
  fetchApi<void>(`/api/dev/tasks/${id}/move`, { method: 'PATCH', body: JSON.stringify(data) });

// Attachments
export function uploadDevAttachment(taskId: number, file: File, options: UploadOptions = {}): Promise<DevTaskAttachment> {
  return uploadToAdmin<DevTaskAttachment>(`/api/dev/tasks/${taskId}/attachments`, file, MAX_SIZE_DEV_ATTACHMENT, options, { 'X-Bolloprint-Admin': '1' });
}
export const deleteDevAttachment = (taskId: number, attId: number) =>
  fetchApi<void>(`/api/dev/tasks/${taskId}/attachments/${attId}`, { method: 'DELETE' });
export const getDevAttachmentUrl = (taskId: number, attId: number) =>
  fetchApi<{ url: string; filename: string; expires_at?: string }>(
    `/api/dev/tasks/${taskId}/attachments/${attId}`,
  );

// Admin users (for assignment selector)
export const getDevAdminUsers = () =>
  fetchApi<{ users: DevAdminUser[] }>('/api/dev/admin-users');

/* ── Master API Tokens ──────────────────────────────────────────────
 * Backed by the ``platform_api_tokens`` table — these are the master
 * tokens (``bp_mat_<hex>``) that authenticate as platform-admin
 * authority across both admin and tenant APIs. Plaintext is returned
 * ONLY on creation; the list/get endpoints expose just the public
 * prefix.
 */
export type MasterToken = {
  id: number;
  name: string;
  token_prefix: string;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
};
export type MasterTokenWithSecret = MasterToken & { plaintext: string };
export type MasterTokenCreatePayload = {
  name: string;
  expires_at?: string | null;
};

export const listMasterTokens = () =>
  fetchApi<MasterToken[]>('/api/me/tokens');
export const createMasterToken = (data: MasterTokenCreatePayload) =>
  fetchApi<MasterTokenWithSecret>('/api/me/tokens', {
    method: 'POST',
    body: JSON.stringify(data),
  });
/** Soft-revoke (sets revoked_at). Token can no longer authenticate but
 *  the row stays in the table for audit. */
export const revokeMasterToken = (id: number) =>
  fetchApi<{ ok: true }>(`/api/me/tokens/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ revoked: true }),
  });
/** Hard-delete (removes the row). Only allowed for already-revoked or
 *  expired tokens — backend returns 409 otherwise. */
export const deleteMasterToken = (id: number) =>
  fetchApi<{ ok: true }>(`/api/me/tokens/${id}`, { method: 'DELETE' });


/* ── Master inventory catalog (global rows, ``workshop_id IS NULL``) ──
 *
 * Schema mirrors OpenPrintTag (https://specs.openprinttag.org). UUIDs are
 * derived server-side from name/material_name/gtin so the same NFC tag a
 * compliant printer/spool exposes can be matched 1:1 against rows here.
 */
export type MasterManufacturer = {
  id: number;
  uuid: string;
  workshop_id: number | null;
  name: string;
  slug: string;
  website: string | null;
  logo_url: string | null;
  country: string | null;
  countries_of_origin: string[] | null;
  description: string | null;
  material_url_template: string | null;
  material_package_url_template: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
};

export type MasterFilament = {
  id: number;
  uuid: string;
  material_uuid: string;
  material_class: 'FFF';
  workshop_id: number | null;
  slug: string;
  manufacturer_id: number;
  material_type: string | null;
  material_name: string;
  url: string | null;
  gtin: number | null;
  brand_specific_material_id: string | null;
  brand_specific_package_id: string | null;
  primary_color_hex: string | null;
  secondary_colors_hex: string[] | null;
  tags: string[];
  certifications: string[] | null;
  nominal_netto_full_weight: number | null;
  nominal_full_length: number | null;
  filament_diameter: number | null;
  density: string | null;
  hardness: string | null;
  min_print_temperature: number | null;
  max_print_temperature: number | null;
  preheat_temperature: number | null;
  min_bed_temperature: number | null;
  max_bed_temperature: number | null;
  chamber_temperature: number | null;
  /** Vendor-recommended max print speed (mm/s). Filament-only. */
  max_print_speed: number | null;
  drying_temperature: number | null;
  drying_time: number | null;
  image_url: string | null;
  image_source_url: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
};

export type MasterResin = {
  id: number;
  uuid: string;
  material_uuid: string;
  material_class: 'SLA';
  workshop_id: number | null;
  slug: string;
  manufacturer_id: number;
  material_type: string | null;
  material_name: string;
  url: string | null;
  gtin: number | null;
  brand_specific_material_id: string | null;
  brand_specific_package_id: string | null;
  primary_color_hex: string | null;
  secondary_colors_hex: string[] | null;
  tags: string[];
  certifications: string[] | null;
  nominal_netto_full_weight: number | null;
  container_volumetric_capacity: number | null;
  density: string | null;
  hardness: string | null;
  cure_wavelength: number | null;
  viscosity: string | null;
  image_url: string | null;
  image_source_url: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
};

/** Slim list-view projection returned by the `/api/master-filaments` list —
 *  only the fields CatalogoPage's table + search render. The edit modal fetches
 *  the full `MasterFilament` via getMasterFilament(id). Mirrors the backend
 *  `_FILAMENT_LIST_FIELDS`. */
export type MasterFilamentListItem = Pick<MasterFilament,
  | 'id' | 'manufacturer_id' | 'material_type' | 'material_name'
  | 'primary_color_hex' | 'secondary_colors_hex' | 'tags'
  | 'filament_diameter' | 'nominal_netto_full_weight'
  | 'brand_specific_material_id' | 'brand_specific_package_id' | 'gtin'
  | 'image_url' | 'is_active'>;

/** Slim list-view projection returned by the `/api/master-resins` list. Mirrors
 *  the backend `_RESIN_LIST_FIELDS`. */
export type MasterResinListItem = Pick<MasterResin,
  | 'id' | 'manufacturer_id' | 'material_type' | 'material_name'
  | 'primary_color_hex' | 'secondary_colors_hex' | 'tags'
  | 'nominal_netto_full_weight'
  | 'brand_specific_material_id' | 'brand_specific_package_id' | 'gtin'
  | 'image_url' | 'is_active'>;

export const listMasterManufacturers = () =>
  fetchApi<{ manufacturers: MasterManufacturer[] }>('/api/master-manufacturers')
    .then(r => r.manufacturers);
export const createMasterManufacturer = (
  data: Partial<MasterManufacturer>,
) =>
  fetchApi<MasterManufacturer>('/api/master-manufacturers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
export const updateMasterManufacturer = (id: number, data: Partial<MasterManufacturer>) =>
  fetchApi<MasterManufacturer>(`/api/master-manufacturers/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
export const deleteMasterManufacturer = (id: number) =>
  fetchApi<{ message: string }>(`/api/master-manufacturers/${id}`, { method: 'DELETE' });
export const uploadMasterManufacturerImage = (id: number, file: File, options: UploadOptions = {}): Promise<MasterManufacturer> =>
  uploadToAdmin<MasterManufacturer>(`/api/master-manufacturers/${id}/image`, file, MAX_SIZE_IMAGE, options);
export const clearMasterManufacturerImage = (id: number) =>
  fetchApi<MasterManufacturer>(`/api/master-manufacturers/${id}/image`, { method: 'DELETE' });

export const listMasterFilaments = (params: {
  manufacturerId?: number;
  materialType?: string;
  onlyActive?: boolean;
  limit?: number;
} = {}) => {
  const qs = new URLSearchParams();
  if (params.manufacturerId) qs.set('manufacturer_id', String(params.manufacturerId));
  if (params.materialType) qs.set('material_type', params.materialType);
  if (params.onlyActive) qs.set('only_active', 'true');
  qs.set('limit', String(params.limit ?? 10000));
  return fetchApi<{ filaments: MasterFilamentListItem[] }>(`/api/master-filaments?${qs}`).then(r => r.filaments);
};
/** Full spec sheet for the edit modal (the list returns a slim projection). */
export const getMasterFilament = (id: number) =>
  fetchApi<MasterFilament>(`/api/master-filaments/${id}`);
export const createMasterFilament = (data: Partial<MasterFilament>) =>
  fetchApi<MasterFilament>('/api/master-filaments', { method: 'POST', body: JSON.stringify(data) });
export const updateMasterFilament = (id: number, data: Partial<MasterFilament>) =>
  fetchApi<MasterFilament>(`/api/master-filaments/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteMasterFilament = (id: number) =>
  fetchApi<{ message: string }>(`/api/master-filaments/${id}`, { method: 'DELETE' });
export const uploadMasterFilamentImage = (id: number, file: File, options: UploadOptions = {}): Promise<MasterFilament> =>
  uploadToAdmin<MasterFilament>(`/api/master-filaments/${id}/image`, file, MAX_SIZE_IMAGE, options);
export const clearMasterFilamentImage = (id: number) =>
  fetchApi<MasterFilament>(`/api/master-filaments/${id}/image`, { method: 'DELETE' });

export const listMasterResins = (params: {
  manufacturerId?: number;
  materialType?: string;
  onlyActive?: boolean;
  limit?: number;
} = {}) => {
  const qs = new URLSearchParams();
  if (params.manufacturerId) qs.set('manufacturer_id', String(params.manufacturerId));
  if (params.materialType) qs.set('material_type', params.materialType);
  if (params.onlyActive) qs.set('only_active', 'true');
  qs.set('limit', String(params.limit ?? 10000));
  return fetchApi<{ resins: MasterResinListItem[] }>(`/api/master-resins?${qs}`).then(r => r.resins);
};
/** Full spec sheet for the edit modal (the list returns a slim projection). */
export const getMasterResin = (id: number) =>
  fetchApi<MasterResin>(`/api/master-resins/${id}`);
export const createMasterResin = (data: Partial<MasterResin>) =>
  fetchApi<MasterResin>('/api/master-resins', { method: 'POST', body: JSON.stringify(data) });
export const updateMasterResin = (id: number, data: Partial<MasterResin>) =>
  fetchApi<MasterResin>(`/api/master-resins/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
export const deleteMasterResin = (id: number) =>
  fetchApi<{ message: string }>(`/api/master-resins/${id}`, { method: 'DELETE' });
export const uploadMasterResinImage = (id: number, file: File, options: UploadOptions = {}): Promise<MasterResin> =>
  uploadToAdmin<MasterResin>(`/api/master-resins/${id}/image`, file, MAX_SIZE_IMAGE, options);
export const clearMasterResinImage = (id: number) =>
  fetchApi<MasterResin>(`/api/master-resins/${id}/image`, { method: 'DELETE' });

/** OpenPrintTag tag vocabulary (+ `tough`) — must mirror `bolloprint_shared.master_material.VALID_TAGS`.
 *  The filament TYPES are `MASTER_FILAMENT_TYPES` in `@shared`. */
export const MASTER_TAGS = [
  'filtration_recommended', 'biocompatible', 'home_compostable',
  'industrially_compostable', 'bio_based', 'antibacterial',
  'air_filtering', 'abrasive', 'foaming', 'castable',
  'self_extinguishing', 'paramagnetic', 'radiation_shielding',
  'high_temperature', 'high_speed', 'esd_safe', 'conductive',
  'emi_shielding', 'blend', 'water_soluble', 'ipa_soluble',
  'limonene_soluble', 'low_outgassing', 'matte', 'silk',
  'translucent', 'transparent', 'water_washable', 'without_pigments', 'iridescent',
  'pearlescent', 'glitter', 'glow_in_the_dark', 'neon',
  'illuminescent_color_change', 'temperature_color_change',
  'gradual_color_change', 'coextruded', 'contains_carbon',
  'contains_carbon_fiber', 'contains_carbon_nano_tubes',
  'contains_graphene', 'contains_glass', 'contains_glass_fiber',
  'contains_kevlar', 'contains_ptfe', 'contains_stone',
  'contains_magnetite', 'contains_organic_material',
  'contains_cork', 'contains_wax', 'contains_wood',
  'contains_algae', 'contains_bamboo', 'contains_pine',
  'contains_ceramic', 'contains_boron_carbide', 'contains_metal',
  'contains_bronze', 'contains_iron', 'contains_steel',
  'contains_silver', 'contains_copper', 'contains_aluminium',
  'contains_brass', 'contains_tungsten', 'imitates_wood',
  'imitates_metal', 'imitates_marble', 'imitates_stone',
  'lithophane', 'recycled', 'limited_edition',
  // Not OpenPrintTag: a tough grade of another family (Siraya Tenacious).
  'tough',
] as const;

export const MASTER_CERTIFICATIONS = ['ul_2818', 'ul_94_v0', 'ul_2904'] as const;

/* ── Financiero (platform finances) ── */
export type FinanceSupplier = {
  id: number;
  supplier_number: number;
  name: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  customer_code: string | null;
  notes: string | null;
  is_system: boolean;
};

export type FinanceExpenseFile = {
  id: number;
  file_type: string | null;
  file_name: string | null;
  file_url: string | null;
  file_size: string | null;
};

export type FinanceExpense = {
  id: number;
  expense_number: number;
  description: string | null;
  date: string | null;
  amount: number;
  delivery_note: string | null;
  invoice_number: string | null;
  invoice_date: string | null;
  supplier_id: number | null;
  source: string;
  supplier: FinanceSupplier | null;
  files: FinanceExpenseFile[];
};

export type FinancePeriod = {
  income_gross: number;
  income_net: number;
  polar_fees: number;
  expenses: number;
  profit: number;
};

export type FinanceSummary = { month: FinancePeriod; year: FinancePeriod; historical: FinancePeriod };

export type FinanceHistoryItem = {
  month: string;
  income_gross: number;
  income_net: number;
  expenses: number;
  profit: number;
};

export type FinanceDetailItem = {
  id: number;
  date: string;
  title: string;
  subtitle: string | null;
  amount: number;
  type: 'polar' | 'expense';
  status: string | null;
};

export type PolarTransaction = {
  id: number;
  type: 'subscription_payment' | 'one_time_payment' | 'refund';
  polar_order_id: string | null;
  polar_subscription_id: string | null;
  workshop_id: number | null;
  client_name: string | null;
  workshop_slug: string | null;
  gross_amount: number;
  tax_amount: number;
  fee_amount: number;
  net_amount: number;
  net_ex_tax: number;
  currency: string;
  billing_reason: string | null;
  description: string | null;
  occurred_at: string;
  plan_name: string | null;
  interval: string | null;
  list_base_ex_tax: number | null;
  discount_amount: number;
  discount_id: string | null;
  period_start: string | null;
  period_end: string | null;
};

/** Suscripción en riesgo (``past_due``), de cualquier proveedor. El backend
 *  ya mandaba ``provider``/``amount``/``currency``/``external_subscription_id``
 *  y el tipo los ocultaba. ``monthly_amount`` es ``plan.price_monthly`` en
 *  EUR (lista), no lo que cobra el proveedor: eso es ``amount``/``currency``. */
export type SubscriptionAtRisk = {
  id: number;
  status: string;
  workshop_id: number | null;
  client_name: string | null;
  workshop_slug: string | null;
  plan_name: string | null;
  provider: string | null;
  external_subscription_id: string | null;
  amount: number | null;
  currency: string | null;
  monthly_amount: number | null;
  period_end: string | null;
  polar_subscription_id: string | null;
};

export type ExpenseAnalysis = {
  description: string | null;
  amount: number | null;
  date: string | null;
  invoice_date: string | null;
  invoice_number: string | null;
  delivery_note: string | null;
  supplier_name: string | null;
  supplier_id: number | null;
};

export type FinanceExpenseFileInput = {
  file_type?: string | null;
  file_name?: string | null;
  file_url?: string | null;
  file_size?: string | null;
};

export type FinanceExpenseInput = {
  description?: string | null;
  date?: string | null;
  amount?: number;
  delivery_note?: string | null;
  invoice_number?: string | null;
  invoice_date?: string | null;
  supplier_id?: number | null;
  files?: FinanceExpenseFileInput[];
};

export const getFinanceSuppliers = () =>
  fetchApi<FinanceSupplier[]>('/api/financiero/suppliers');
export const createFinanceSupplier = (data: Partial<FinanceSupplier>) =>
  fetchApi<FinanceSupplier>('/api/financiero/suppliers', { method: 'POST', body: JSON.stringify(data) });
export const updateFinanceSupplier = (id: number, data: Partial<FinanceSupplier>) =>
  fetchApi<FinanceSupplier>(`/api/financiero/suppliers/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteFinanceSupplier = (id: number) =>
  fetchApi<{ ok: boolean }>(`/api/financiero/suppliers/${id}`, { method: 'DELETE' });

export const getFinanceExpenses = () =>
  fetchApi<FinanceExpense[]>('/api/financiero/expenses');
export const createFinanceExpense = (data: FinanceExpenseInput) =>
  fetchApi<FinanceExpense>('/api/financiero/expenses', { method: 'POST', body: JSON.stringify(data) });
export const updateFinanceExpense = (id: number, data: FinanceExpenseInput) =>
  fetchApi<FinanceExpense>(`/api/financiero/expenses/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const deleteFinanceExpense = (id: number) =>
  fetchApi<{ ok: boolean }>(`/api/financiero/expenses/${id}`, { method: 'DELETE' });

export const getFinanceSummary = () => fetchApi<FinanceSummary>('/api/financiero/summary');
export const getFinanceHistory = () => fetchApi<FinanceHistoryItem[]>('/api/financiero/history');
export const getFinanceDetails = (metric: string, period: string) =>
  fetchApi<{ items: FinanceDetailItem[]; total_amount: number }>(
    `/api/financiero/details?metric=${metric}&period=${period}`,
  );
export const getPolarTransactions = (type?: string, workshopId?: number) => {
  const params = new URLSearchParams();
  if (type) params.set('type', type);
  if (workshopId !== undefined) params.set('workshop_id', String(workshopId));
  const qs = params.toString();
  return fetchApi<PolarTransaction[]>(`/api/financiero/polar/transactions${qs ? `?${qs}` : ''}`);
};
export const getPolarAtRisk = () => fetchApi<SubscriptionAtRisk[]>('/api/financiero/polar/at-risk');

/** Email delivery audit (Resend webhook → email_deliveries table). */
export type EmailDeliveryRow = {
  id: number;
  message_id: string;
  to_email: string;
  subject: string | null;
  status: string;
  sent_at: string | null;
  last_event_at: string | null;
  error_text: string | null;
  workshop_id: number | null;
  client_name: string | null;
  workshop_slug: string | null;
};

export type EmailDeliveryStats = {
  window_hours: number;
  total: number;
  by_status: Record<string, number>;
  bounce_rate: number;
};

export const getEmailDeliveries = (params = '') =>
  fetchApi<{ deliveries: EmailDeliveryRow[]; total: number }>(`/api/resend/deliveries?${params}`);
export const getEmailDeliveryStats = () =>
  fetchApi<EmailDeliveryStats>('/api/resend/deliveries/stats');

/** Un evento de facturación (GET /api/billing/events). Hoy sólo Polar: Shopify
 * se retiró el 2026-10-21. */
export type BillingEventRow = {
  provider: 'polar';
  /** webhook_id de Polar. */
  ref: string;
  event_id: number | null;
  event_type: string | null;
  occurred_at: string | null;
  status: 'ok' | 'warning' | 'error' | 'unknown';
  raw_status: string | null;
  detail: string | null;
  workshop_id: number | null;
  workshop_name: string | null;
  workshop_slug: string | null;
  can_replay: boolean;
};
export const getBillingEvents = (params = '') =>
  fetchApi<{ events: BillingEventRow[]; total: number; offset: number; limit: number }>(`/api/billing/events?${params}`);

/* ── Búsqueda global (paleta Ctrl/Cmd+K) ── */
export type SearchResponse = {
  q: string;
  workshops: { id: number; name: string; slug: string; owner_email: string | null; plan_name: string | null; deleted: boolean }[];
  users: { id: number; email: string | null; name: string | null; is_active: boolean }[];
  subscriptions: { id: number; workshop_id: number; workshop_name: string; workshop_slug: string; plan_name: string | null; provider: string | null; display_state: string }[];
};
export const searchGlobal = (q: string) =>
  fetchApi<SearchResponse>(`/api/search?q=${encodeURIComponent(q)}`);

/* ── Monitor: el Registro de eventos (GET /api/monitor/*) ──
 * El catálogo de tipos vive en el backend (bolloprint_shared.event_catalog):
 * etiquetas, categorías y severidades salen de getMonitorCatalog(), nunca de un
 * mapa escrito aquí. La severidad y la categoría de una fila son las SUYAS
 * (pueden no ser las del tipo), y hay filas `legacy.*` que el catálogo no nombra. */
export type MonitorSeverity = 'info' | 'success' | 'warning' | 'error' | 'critical';
export type MonitorSource = 'admin' | 'tenant' | 'worker' | 'webhook' | 'scheduler' | 'web';
export type MonitorCategory = { key: string; label: string };
export type MonitorType = {
  type: string;
  category: string;
  /** Severidad POR DEFECTO del tipo; cada fila lleva la suya. */
  severity: MonitorSeverity;
  label: string;
  notifiable: boolean;
  audience: 'team' | 'customer';
  /** Tipo de alarma que ABRE una fila de este tipo (la clave estática), o null. */
  opens_alarm: string | null;
  /** Tipo de alarma que CIERRA. Igual que `opens_alarm` en los de ida y vuelta. */
  closes_alarm: string | null;
  /** Minutos sin repetirse tras los que la alarma se resuelve sola; null = a mano. */
  auto_resolve_min: number | null;
  /** Tipos de detector: sólo avisan como alarma, nunca «cada evento». */
  alarm_only: boolean;
};
/** Un tipo de alarma: la parte estática de su clave, con lo que la describe. */
export type MonitorAlarmKind = {
  kind: string;
  label: string;
  category: string;
  /** La del abridor más grave. */
  severity: MonitorSeverity;
  auto_resolve_min: number | null;
};
export type MonitorCatalog = {
  categories: MonitorCategory[];
  severities: MonitorSeverity[];
  types: MonitorType[];
  alarm_kinds: MonitorAlarmKind[];
};
export type MonitorEventRow = {
  id: number;
  event_type: string;
  /** Etiqueta del catálogo; para un tipo desconocido (`legacy.*`), el propio tipo. */
  label: string;
  category: string;
  category_label: string;
  severity: MonitorSeverity;
  source: MonitorSource;
  description: string;
  workshop_id: number | null;
  workshop_name: string | null;
  workshop_slug: string | null;
  user_id: number | null;
  /** Nombre del token (peticiones PAT) o username/email del admin; null en eventos de sistema. */
  performed_by: string | null;
  actor_label: string | null;
  request_id: string | null;
  created_at: string | null;
};
export type MonitorEventDetail = MonitorEventRow & {
  metadata: Record<string, unknown>;
  dedupe_key: string | null;
  alarm_id: number | null;
};
export type MonitorEventsResponse = {
  items: MonitorEventRow[];
  total: number;
  limit: number;
  offset: number;
};
export type MonitorEventsParams = {
  category?: string[];
  severity?: MonitorSeverity[];
  source?: MonitorSource[];
  type?: string[];
  workshopId?: number | null;
  userId?: number | null;
  partnerId?: number | null;
  /** Sólo eventos de plataforma (sin taller); gana a workshopId. */
  platformOnly?: boolean;
  /** YYYY-MM-DD, inclusivo (UTC). */
  dateFrom?: string;
  /** YYYY-MM-DD, inclusivo (UTC). */
  dateTo?: string;
  q?: string;
  /** «Desarrollo» queda fuera salvo que se pida aquí o en category/type. */
  includeDev?: boolean;
  /** 1..100 (por defecto 50). */
  limit?: number;
  offset?: number;
};

export const getMonitorCatalog = () => fetchApi<MonitorCatalog>('/api/monitor/catalog');
export const getMonitorEvents = (params: MonitorEventsParams = {}) => {
  const qs = new URLSearchParams();
  if (params.category?.length) qs.set('category', params.category.join(','));
  if (params.severity?.length) qs.set('severity', params.severity.join(','));
  if (params.source?.length) qs.set('source', params.source.join(','));
  if (params.type?.length) qs.set('type', params.type.join(','));
  if (params.workshopId != null) qs.set('workshop_id', String(params.workshopId));
  if (params.userId != null) qs.set('user_id', String(params.userId));
  if (params.partnerId != null) qs.set('partner_id', String(params.partnerId));
  if (params.platformOnly) qs.set('platform_only', 'true');
  if (params.dateFrom) qs.set('date_from', params.dateFrom);
  if (params.dateTo) qs.set('date_to', params.dateTo);
  if (params.q?.trim()) qs.set('q', params.q.trim());
  if (params.includeDev) qs.set('include_dev', 'true');
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return fetchApi<MonitorEventsResponse>(`/api/monitor/events${suffix}`);
};
export const getMonitorEvent = (id: number) =>
  fetchApi<MonitorEventDetail>(`/api/monitor/events/${id}`);

/* ── Monitor: las alarmas (GET/POST /api/monitor/alarms*, /api/monitor/settings) ──
 * Una alarma es un problema con estado (abierta → reconocida / silenciada →
 * resuelta) que agrupa los eventos que lo repiten. Sólo el motor del admin las
 * abre y cierra; la pantalla sólo pide y manda acciones. Toda acción manda un
 * cuerpo JSON (`{}` como mínimo): el backend lo exige para que un POST de otra
 * web sin JSON no pueda actuar. */
export type AlarmState = 'open' | 'acknowledged' | 'silenced' | 'resolved';
export type AlarmResolveReason = 'event' | 'cleared' | 'timeout' | 'manual';
export type AlarmSeverity = 'warning' | 'error' | 'critical';
export interface AlarmUserRef {
  id: number;
  name: string;
  email: string;
  avatar_url?: string | null;
}
export interface MonitorAlarm {
  id: number;
  alarm_key: string;
  kind: string;
  kind_label: string;
  /** El tipo del evento que la abrió (el de ORIGEN). */
  event_type: string;
  event_label: string;
  category: string;
  category_label: string;
  severity: MonitorSeverity;
  state: AlarmState;
  title: string;
  /** Ruta interna del SPA; pasar por `isInternalLink` antes de enlazarla. */
  link: string | null;
  workshop_id: number | null;
  workshop_slug: string | null;
  workshop_name: string | null;
  /** Veces que se ha visto. */
  count: number;
  /** Sube en cada apertura y en cada escalado: un «Reconocer» sólo vale para la suya. */
  open_seq: number;
  reopened_count: number;
  first_seen_at: string;
  last_seen_at: string;
  acknowledged_at: string | null;
  acknowledged_by: AlarmUserRef | null;
  silenced_until: string | null;
  silenced_at: string | null;
  silenced_by: AlarmUserRef | null;
  resolved_at: string | null;
  resolved_by: AlarmUserRef | null;
  resolve_reason: AlarmResolveReason | null;
  assigned_to: AlarmUserRef | null;
  /** null = no se resuelve sola. */
  auto_resolve_after_min: number | null;
  sentry_issue_url: string | null;
  /** La lleva un detector con estado: si se resuelve a mano y la condición
   *  sigue, vuelve a abrirse al pasar la ventana de reapertura. */
  stateful: boolean;
  notes_count: number;
}
export interface MonitorAlarmNote {
  at: string;
  user_id: number | null;
  user_label: string;
  text: string;
  via: 'admin' | 'ntfy';
}
export interface MonitorAlarmDelivery {
  id: number;
  channel_id: number;
  channel_name: string;
  kind: NotificationDeliveryKind;
  status: string;
  sent_at: string | null;
  last_error: string | null;
  created_at: string;
}
export interface MonitorAlarmActions {
  acknowledge: boolean;
  resolve: boolean;
  silence: boolean;
  unsilence: boolean;
  assign: boolean;
  note: boolean;
}
export interface MonitorAlarmDetail extends MonitorAlarm {
  /** De la más nueva a la más vieja. */
  notes: MonitorAlarmNote[];
  /** Los 20 últimos; el resto, con `getMonitorAlarmEvents`. */
  events: MonitorEventRow[];
  events_total: number;
  /** Los 20 últimos avisos enviados por esta alarma. */
  deliveries: MonitorAlarmDelivery[];
  /** `error`, `workshop_name`, `plan_name`, `models`… donde el detector los puso. */
  metadata: Record<string, unknown>;
  actions: MonitorAlarmActions;
}
export interface MonitorAlarmsParams {
  /** CSV de estados, o `active` (las tres no resueltas; por defecto). */
  state?: string;
  /** CSV. */
  severity?: string;
  /** CSV de tipos de alarma (`alarm_kinds[].kind`). */
  kind?: string;
  /** CSV. */
  category?: string;
  workshopId?: number;
  assigned?: 'me' | 'none' | number;
  /** Dos caracteres como mínimo; busca en título y clave. */
  q?: string;
  /** YYYY-MM-DD, sobre `last_seen_at`. */
  desde?: string;
  hasta?: string;
  sort?: 'severity' | 'last_seen' | 'first_seen';
  /** 1..100 (por defecto 50). */
  limit?: number;
  offset?: number;
}
export interface MonitorAlarmsResponse {
  items: MonitorAlarm[];
  total: number;
  limit: number;
  offset: number;
}
/** Contadores de las no resueltas. `attention` = abiertas + reconocidas (las
 *  silenciadas no cuentan), y `by_severity` y `worst` cuentan ese mismo
 *  conjunto: es lo que enciende la pastilla del menú. */
export interface AlarmSummary {
  open: number;
  acknowledged: number;
  silenced: number;
  attention: number;
  by_severity: { warning: number; error: number; critical: number };
  worst: AlarmSeverity | null;
  /** Asignadas a quien pregunta (entre las de `attention`). */
  mine: number;
}
export type SilenceDuration = '1h' | '1d' | '7d' | 'until';

/* Monitor → Ajustes → Alarmas y retención (PUT con `version`: 409 si cambió entretanto). */
export interface AlarmKindPolicy {
  enabled: boolean;
  severity_override: AlarmSeverity | null;
  /** null = el del catálogo; 0 = nunca; 1..43200. */
  auto_resolve_after_min: number | null;
  reopen: boolean;
}
export interface AlarmPolicy {
  version: number;
  /** 0..10080. */
  reopen_window_min: number;
  /** Sólo los tipos que se apartan de lo de por defecto. */
  kinds: Record<string, AlarmKindPolicy>;
  /** detector → parámetro → valor (`storage_quota.warn_pct`, …). */
  detectors: Record<string, Record<string, number>>;
}
/** Cada valor, 30..3650 días. Se aplica el periodo más largo que toque
 *  (el general, el de su categoría, el de su gravedad). */
export interface RetentionPolicy {
  version: number;
  events_default_days: number;
  events_by_category: Record<string, number>;
  events_by_severity: Record<string, number>;
  alarms_resolved_days: number;
}
export interface AlarmKindInfo {
  kind: string;
  label: string;
  category: string;
  default_severity: MonitorSeverity;
  default_auto_resolve_after_min: number | null;
  /** `event`: la abre un evento; `detector`: un detector periódico. */
  source: 'event' | 'detector';
  stateful: boolean;
}
export interface MonitorSettings {
  alarm_policy: AlarmPolicy;
  retention_policy: RetentionPolicy;
  defaults: { alarm_policy: AlarmPolicy; retention_policy: RetentionPolicy };
  kinds: AlarmKindInfo[];
  categories: MonitorCategory[];
  version: string;
}
export interface MonitorSettingsInput {
  alarm_policy: AlarmPolicy;
  retention_policy: RetentionPolicy;
  /** La del GET (o PUT anterior) del que partió la edición. */
  version: string;
}

export const getMonitorAlarms = (params: MonitorAlarmsParams = {}) => {
  const qs = new URLSearchParams();
  if (params.state) qs.set('state', params.state);
  if (params.severity) qs.set('severity', params.severity);
  if (params.kind) qs.set('kind', params.kind);
  if (params.category) qs.set('category', params.category);
  if (params.workshopId != null) qs.set('workshop_id', String(params.workshopId));
  if (params.assigned != null) qs.set('assigned', String(params.assigned));
  if (params.q?.trim()) qs.set('q', params.q.trim());
  if (params.desde) qs.set('desde', params.desde);
  if (params.hasta) qs.set('hasta', params.hasta);
  if (params.sort) qs.set('sort', params.sort);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return fetchApi<MonitorAlarmsResponse>(`/api/monitor/alarms${suffix}`);
};
export const getAlarmSummary = () =>
  fetchApi<AlarmSummary>('/api/monitor/alarms/summary');
export const getMonitorAlarm = (id: number) =>
  fetchApi<MonitorAlarmDetail>(`/api/monitor/alarms/${id}`);
export const getMonitorAlarmEvents = (id: number, limit = 20, offset = 0) =>
  fetchApi<MonitorEventsResponse>(`/api/monitor/alarms/${id}/events?limit=${limit}&offset=${offset}`);

const postAlarm = <T,>(id: number, action: string, body: object = {}) =>
  fetchApi<T>(`/api/monitor/alarms/${id}/${action}`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
export const acknowledgeAlarm = (id: number, note?: string) =>
  postAlarm<{ alarm: MonitorAlarm; already: boolean }>(id, 'acknowledge', note ? { note } : {});
export const resolveAlarm = (id: number, note?: string) =>
  postAlarm<{ alarm: MonitorAlarm }>(id, 'resolve', note ? { note } : {});
/** `until` (ISO-8601) sólo con `duration: 'until'`: futura y como mucho a 90 días. */
export const silenceAlarm = (id: number, duration: SilenceDuration, until?: string) =>
  postAlarm<{ alarm: MonitorAlarm }>(id, 'silence', until ? { duration, until } : { duration });
export const unsilenceAlarm = (id: number) =>
  postAlarm<{ alarm: MonitorAlarm }>(id, 'unsilence');
/** `null` la deja sin asignar. El usuario tiene que ser administrador de plataforma activo. */
export const assignAlarm = (id: number, userId: number | null) =>
  postAlarm<{ alarm: MonitorAlarm }>(id, 'assign', { user_id: userId });
export const addAlarmNote = (id: number, text: string) =>
  postAlarm<{ alarm: MonitorAlarm; note: MonitorAlarmNote }>(id, 'notes', { text });

export const getMonitorSettings = () =>
  fetchApi<MonitorSettings>('/api/monitor/settings');
/** Guardar sin cambios no escribe ni emite nada. 409 (`isApiStatus(err, 409)`)
 *  si lo guardado cambió desde `version`; 422 con el primer fallo. */
export const putMonitorSettings = (body: MonitorSettingsInput) =>
  fetchApi<MonitorSettings>('/api/monitor/settings', {
    method: 'PUT',
    body: JSON.stringify(body),
  });
/** Renueva la clave con la que se firman los «Reconocer»: los ya enviados dejan de valer. */
export const rotateAckSecret = () =>
  fetchApi<{ ok: boolean }>('/api/monitor/settings/rotate-ack-secret', {
    method: 'POST',
    body: '{}',
  });

/* ── Notificaciones por ntfy (Monitor → Ajustes → Avisos al móvil) ──
 * Sólo el admin publica en ntfy y sólo para el equipo. Con `ntfy_enabled`
 * ausente todo esto funciona sin servidor: /status dice «apagado» y /test
 * contesta ok=false sin llamar a nada. */
export type NotificationStatus = {
  enabled: boolean;
  configured: boolean;
  /** Último `platform_events.id` que el repartidor ya miró. */
  cursor: number;
  pending: number;
  failed_24h: number;
  last_sent_at: string | null;
};
export type NotificationTestResult = {
  ok: boolean;
  status_code: number | null;
  error: string | null;
};
export type NotificationChannel = {
  id: number;
  name: string;
  topic: string;
  /** 1..5; null = la prioridad por defecto del servidor. */
  priority: number | null;
  /** Etiquetas de ntfy separadas por comas. */
  tags: string | null;
  enabled: boolean;
  /** El aviso de una alarma lleva el botón «Reconocer»: quien lea el tema la
   *  reconoce sin entrar en el admin. Apagado por defecto. */
  allow_ack: boolean;
  rules_count: number;
  created_at: string | null;
  updated_at: string | null;
};
export type NotificationChannelInput = {
  name: string;
  topic: string;
  priority?: number | null;
  tags?: string | null;
  enabled?: boolean;
  allow_ack?: boolean;
};
/** Severidad mínima de una regla. El backend acepta las cinco; `success`
 *  cuenta igual que `info` (el repartidor las ordena al mismo nivel), así que
 *  la pantalla no la ofrece pero sí la enseña si una regla la trae. */
export type NotificationMinSeverity = 'info' | 'success' | 'warning' | 'error' | 'critical';
export type NotificationRule = {
  channel_id: number;
  match_kind: 'type' | 'category';
  match_value: string;
  min_severity: NotificationMinSeverity;
  /** Tope de avisos por hora; incluye los de alarma, y al pasarlo se agrupan
   *  en uno. null = sin límite. */
  max_per_hour: number | null;
  enabled: boolean;
  /** Cuándo avisa; al menos uno a true. `on_event`: cada evento (no vale en
   *  los tipos `alarm_only`). Los de alarma sólo valen donde el tipo, o algún
   *  tipo de la categoría, abre alarmas, y se evalúan contra el tipo de ORIGEN. */
  on_event: boolean;
  on_alarm_open: boolean;
  on_alarm_resolve: boolean;
};
/** La matriz entera y su `version`: un hash corto de lo guardado que el PUT
 *  tiene que devolver (409 si cambió entretanto). */
export type NotificationRulesResponse = {
  rules: NotificationRule[];
  version: string;
};
export type NotificationDeliveryStatus = 'pending' | 'sent' | 'failed' | 'skipped';
/** `event` (un aviso por evento), `alarm_open` / `alarm_resolve` (el aviso de
 *  una alarma) o `digest` (el resumen de los que pasaron del tope). */
export type NotificationDeliveryKind = 'event' | 'alarm_open' | 'alarm_resolve' | 'digest';
export type NotificationDelivery = {
  id: number;
  event_id: number;
  event_type: string;
  event_label: string;
  event_description: string | null;
  channel_id: number;
  channel_name: string;
  kind: NotificationDeliveryKind;
  /** La alarma de un aviso `alarm_open` / `alarm_resolve`; null en el resto. */
  alarm_id: number | null;
  status: NotificationDeliveryStatus;
  attempts: number;
  /** El fallo, o el motivo por el que se omitió (`notification_dispatcher`). */
  last_error: string | null;
  next_attempt_at: string | null;
  sent_at: string | null;
  created_at: string | null;
};
export type NotificationDeliveriesParams = {
  status?: NotificationDeliveryStatus | '';
  channelId?: number | null;
  /** 1..100 (por defecto 50). */
  limit?: number;
  offset?: number;
};

export const getNotificationStatus = () =>
  fetchApi<NotificationStatus>('/api/notifications/status');
export const sendNotificationTest = (topic: string) =>
  fetchApi<NotificationTestResult>('/api/notifications/test', {
    method: 'POST',
    body: JSON.stringify({ topic }),
  });
export const listNotificationChannels = () =>
  fetchApi<{ items: NotificationChannel[] }>('/api/notifications/channels');
export const createNotificationChannel = (body: NotificationChannelInput) =>
  fetchApi<NotificationChannel>('/api/notifications/channels', {
    method: 'POST',
    body: JSON.stringify(body),
  });
export const updateNotificationChannel = (id: number, body: Partial<NotificationChannelInput>) =>
  fetchApi<NotificationChannel>(`/api/notifications/channels/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
export const deleteNotificationChannel = (id: number) =>
  fetchApi<{ ok: boolean }>(`/api/notifications/channels/${id}`, { method: 'DELETE' });
export const testNotificationChannel = (id: number) =>
  fetchApi<NotificationTestResult>(`/api/notifications/channels/${id}/test`, { method: 'POST' });
export const getNotificationRules = () =>
  fetchApi<NotificationRulesResponse>('/api/notifications/rules');
/**
 * Sustituye la matriz ENTERA (una transacción en el backend). `version` es la
 * del GET (o del PUT anterior) de la que partió la edición: si lo guardado
 * cambió entretanto, 409 (`isApiStatus(err, 409)`) y no se escribe nada.
 */
export const putNotificationRules = (rules: NotificationRule[], version: string) =>
  fetchApi<NotificationRulesResponse>('/api/notifications/rules', {
    method: 'PUT',
    body: JSON.stringify({ rules, version }),
  });
export const getNotificationDeliveries = (params: NotificationDeliveriesParams = {}) => {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.channelId != null) qs.set('channel_id', String(params.channelId));
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return fetchApi<{ items: NotificationDelivery[]; total: number }>(`/api/notifications/deliveries${suffix}`);
};
/** Vuelve a poner en cola una entrega. Sólo `failed`, o `skipped` que no
 *  llegó ya dentro de otro aviso (`canRetryDelivery` en
 *  `pages/config/NotificationsSection.tsx`); si no, 409 con el motivo. */
export const retryNotificationDelivery = (id: number) =>
  fetchApi<{ ok: boolean }>(`/api/notifications/deliveries/${id}/retry`, { method: 'POST' });

/** Webhook event audit (see admin → Suscripciones → Eventos Polar). */
export const getPolarEvents = (params = '') =>
  fetchApi<{ events: PolarEventRow[]; total: number }>(`/api/polar/events?${params}`);
export const getPolarEvent = (webhookId: string) =>
  fetchApi<PolarEventDetail>(`/api/polar/events/${encodeURIComponent(webhookId)}`);
export const replayPolarEvent = (webhookId: string) =>
  fetchApi<{ ok: boolean; webhook_id: string; status: string }>(
    `/api/polar/events/${encodeURIComponent(webhookId)}/replay`,
    { method: 'POST' },
  );
/** «Descartar»: cierra un evento en `error` que ningún reproceso arregla
 * (pasa a `dismissed`) y con él su alarma del Monitor. No lo reprocesa.
 * 409 si el evento no está en error; `already` si ya estaba descartado. */
export const dismissPolarEvent = (webhookId: string, note?: string) =>
  fetchApi<{ ok: boolean; already: boolean; webhook_id: string; status: string }>(
    `/api/polar/events/${encodeURIComponent(webhookId)}/dismiss`,
    { method: 'POST', body: JSON.stringify(note ? { note } : {}) },
  );

/**
 * Rescue path for a paid checkout whose workshop was never created (the user
 * closed the tab before the browser called /create-workshop). The context call
 * returns everything the checkout metadata already knows so the operator only
 * types what it cannot tell us — name, country and the fiscal/address block.
 */
export type ProvisionContext = {
  webhook_id: string;
  status: string | null;
  slug: string;
  slug_taken: boolean;
  interval: string;
  region: string;
  polar_subscription_id: string | null;
  already_bound: boolean;
  plan: { id: number; name: string } | null;
  owner: { id: number; email: string; name: string } | null;
  customer_email: string;
};

export type ProvisionWorkshopPayload = {
  name: string;
  country: string;
  owner_user_id?: number;
  slug?: string;
  workshop_type?: string;
  acquisition_source?: string;
  acquisition_source_other?: string;
  tax_id?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  address_line1?: string;
  address_line2?: string;
};

export const getProvisionContext = (webhookId: string) =>
  fetchApi<ProvisionContext>(
    `/api/polar/events/${encodeURIComponent(webhookId)}/provision-context`,
  );

export const provisionWorkshopFromEvent = (
  webhookId: string,
  payload: ProvisionWorkshopPayload,
) =>
  fetchApi<{
    ok: boolean;
    workshop_id: number;
    slug: string;
    tenant_origin: string;
    storage_initialized: boolean;
    replay_error: string | null;
  }>(`/api/polar/events/${encodeURIComponent(webhookId)}/provision`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const analyzeFinanceExpense = async (file: File): Promise<ExpenseAnalysis> => {
  if (file.size > MAX_SIZE_IMAGE) throw new Error(tooLargeMessage(MAX_SIZE_IMAGE));
  const fd = new FormData();
  fd.append('file', file);
  const res = await safeUploadFetch(withApiBaseUrl('/api/financiero/ai/analyze-expense'), {
    method: 'POST', credentials: 'include', body: fd,
  }, file);
  if (!res.ok) throw new Error(extractApiError(await res.json().catch(() => ({})), res.status));
  return res.json();
};


// ── Avisos del taller (documentation/Workshop_Notices.md §5) ─────────────────

export type NoticeTone = 'red' | 'yellow' | 'white';
export type NoticeTypeState = 'active' | 'test_only' | 'off';

export interface WorkshopNoticeType {
  type: string;
  number: number;
  category: string;
  label: string;
  tone: NoticeTone;
  tones: NoticeTone[];
  audience: 'all' | 'config' | 'owner' | 'actor';
  kind: 'state' | 'group' | 'event' | 'countdown';
  source: 'detect' | 'scan';
  i18n: string;
  action: string;
  mutable: boolean;
  mute_scope: 'type' | 'subject';
  expires_hours: number | null;
  first_version: boolean;
  default_thresholds: Record<string, number>;
  thresholds: Record<string, number>;
  state: NoticeTypeState;
  /** Ahora: avisos abiertos, en cuántos talleres y el último visto. */
  now: { open: number; workshops: number; last_seen_at: string | null };
}

export interface WorkshopNoticesCatalog {
  version: string;
  enabled: boolean;
  snooze_days: number;
  default_snooze_days: number;
  categories: { key: string; label: string }[];
  types: WorkshopNoticeType[];
}

export interface WorkshopNoticePolicyInput {
  version: string;
  enabled: boolean;
  snooze_days: number;
  types: Record<string, { state: NoticeTypeState; thresholds: Record<string, number> }>;
}

export const getWorkshopNoticesCatalog = () =>
  fetchApi<WorkshopNoticesCatalog>('/api/workshop-notices');
/** 409 (`isApiStatus(err, 409)`) si la política cambió desde `version`; 422 con
 *  la lista de problemas. Guardar sin cambios no escribe nada. */
export const putWorkshopNoticePolicy = (body: WorkshopNoticePolicyInput) =>
  fetchApi<WorkshopNoticesCatalog>('/api/workshop-notices/policy', {
    method: 'PUT',
    body: JSON.stringify(body),
  });

export interface WorkshopNoticeRow {
  id: number;
  type: string;
  number: number | null;
  label: string;
  category: string | null;
  audience: string | null;
  subject: string;
  tone: NoticeTone;
  stage: number;
  count: number;
  params: Record<string, unknown>;
  opened_at: string | null;
  last_seen_at: string | null;
  resolved_at: string | null;
  expires_at: string | null;
  actor: string | null;
}

export interface WorkshopNoticeSilence {
  kind: 'snoozed' | 'dismissed' | 'muted';
  notice_id: number | null;
  type: string;
  label: string;
  subject: string;
  who: string;
  until: string | null;
  since: string | null;
}

export interface WorkshopNoticeView {
  open: WorkshopNoticeRow[];
  closed: WorkshopNoticeRow[];
  silences: WorkshopNoticeSilence[];
}

export const getWorkshopNoticeView = (workshopId: number) =>
  fetchApi<WorkshopNoticeView>(`/api/workshops/${workshopId}/notices`);
/** «Comprobar todo» de un taller (lo hace el tenant por M2M). */
export const checkWorkshopNotices = (workshopId: number) =>
  fetchApi<{ checked: number; closed: number; unknown: number }>(`/api/workshops/${workshopId}/notices/check`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
